/**
 * Correlogram — Scatter-plot matrix with configurable plot types.
 *
 * N x N grid of mini-charts for selected numeric columns:
 *   Diagonal: histogram | box plot | violin/KDE (distribution)
 *   Off-diagonal: scatter | line | hexbin (bivariate) with Pearson r overlay
 *
 * Performance: zoom/pan is done via direct DOM manipulation (no React
 * re-renders). Grid cells are React.memo'd. Tooltips are only in the
 * enlarged modal, not in the 144-cell grid.
 */

import {
  Fragment,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
  type ReactNode,
} from "react";
import {
  ScatterChart,
  Scatter,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from "recharts";
import Plot from "react-plotly.js";
import {
  binData,
  pearsonCorrelation,
  partialCorrelation,
  multipleCorrelationR,
  gaussianKde,
  computeBoxPlotStats,
  hexBin2d,
  lttbDownsample,
} from "./useHistogramBins";
import { useProvenanceContext } from "../../context/ProvenanceContext";

// ---------------------------------------------------------------------------
// Plot type configuration
// ---------------------------------------------------------------------------

export type OffDiagPlotType = "scatter" | "line" | "hexbin" | "scatter3d";
export type DiagPlotType = "histogram" | "boxplot" | "violin";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CorrelogramColumn {
  key: string;
  header: string;
}

interface DataGroup {
  key: string;
  label: string;
  rows: Record<string, unknown>[];
  color: string;
}

interface CorrelogramProps {
  rows: Record<string, unknown>[];
  numericColumns: CorrelogramColumn[];
  groups?: DataGroup[];
  colorByKey?: string;
  sizeByKey?: string;
  symbolByKey?: string;
  /** When set, enables 3D scatter mode: each off-diagonal cell gets a Z dimension. */
  zAxisKey?: string;
  onPointClick?: (rowIdx: number) => void;
  /** Right-click on a data point — passes row index and native event for context menu positioning. */
  onPointContextMenu?: (rowIdx: number, event: React.MouseEvent) => void;
  selectedRowIdx?: number;
  offDiagPlotType?: OffDiagPlotType;
  diagPlotType?: DiagPlotType;
  onOffDiagPlotTypeChange?: (type: OffDiagPlotType) => void;
  onDiagPlotTypeChange?: (type: DiagPlotType) => void;
}

// ---------------------------------------------------------------------------
// Palette
// ---------------------------------------------------------------------------

const SCATTER_COLORS = [
  "#818cf8", "#34d399", "#fbbf24", "#f472b6",
  "#60a5fa", "#a78bfa", "#fb923c", "#2dd4bf",
];

const HISTOGRAM_COLOR = "rgba(99, 102, 241, 0.5)";
const HISTOGRAM_STROKE = "#6366f1";

const GRADIENT_COOL: [number, number, number] = [99, 102, 241];
const GRADIENT_WARM: [number, number, number] = [251, 191, 36];

function gradientColor(t: number): string {
  const r = Math.round(GRADIENT_COOL[0] + t * (GRADIENT_WARM[0] - GRADIENT_COOL[0]));
  const g = Math.round(GRADIENT_COOL[1] + t * (GRADIENT_WARM[1] - GRADIENT_COOL[1]));
  const b = Math.round(GRADIENT_COOL[2] + t * (GRADIENT_WARM[2] - GRADIENT_COOL[2]));
  return `rgb(${r},${g},${b})`;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function extractNumbers(rows: Record<string, unknown>[], key: string): number[] {
  return rows
    .map(r => r[key])
    .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
}

function extractPairs(
  rows: Record<string, unknown>[],
  xKey: string,
  yKey: string,
): { x: number; y: number; idx: number }[] {
  const result: { x: number; y: number; idx: number }[] = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]!;
    const xv = row[xKey];
    const yv = row[yKey];
    if (typeof xv === "number" && Number.isFinite(xv) &&
        typeof yv === "number" && Number.isFinite(yv)) {
      result.push({ x: xv, y: yv, idx: i });
    }
  }
  return result;
}

function extractTriples(
  rows: Record<string, unknown>[],
  xKey: string,
  yKey: string,
  zKey: string,
): { x: number; y: number; z: number; idx: number }[] {
  const result: { x: number; y: number; z: number; idx: number }[] = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]!;
    const xv = row[xKey], yv = row[yKey], zv = row[zKey];
    if (typeof xv === "number" && Number.isFinite(xv) &&
        typeof yv === "number" && Number.isFinite(yv) &&
        typeof zv === "number" && Number.isFinite(zv)) {
      result.push({ x: xv, y: yv, z: zv, idx: i });
    }
  }
  return result;
}

function shortLabel(val: number): string {
  if (Math.abs(val) >= 10000 || (Math.abs(val) < 0.01 && val !== 0)) {
    return val.toExponential(1);
  }
  if (Number.isInteger(val)) return val.toLocaleString();
  return val.toFixed(2);
}

function rColor(r: number): string {
  const abs = Math.abs(r);
  if (abs > 0.7) return "#34d399";
  if (abs > 0.4) return "#fbbf24";
  return "#6b7280";
}

/** Color for partial correlation badge — blue means z explains the coupling, green means axes are independently coupled. */
function partialRColor(r: number, rPartial: number): string {
  const abs = Math.abs(rPartial);
  const drop = Math.abs(r) - abs;
  // Large drop from r to r_partial → z explains the coupling (blue)
  if (drop > 0.3) return "#60a5fa";
  // Still strongly correlated after controlling for z → independent coupling (green)
  if (abs > 0.5) return "#34d399";
  return "#6b7280";
}

interface ColRange { min: number; max: number }

function computeRange(values: number[]): ColRange | null {
  if (values.length === 0) return null;
  let min = Infinity, max = -Infinity;
  for (const v of values) {
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return { min, max };
}

// ---------------------------------------------------------------------------
// Layout constants
// ---------------------------------------------------------------------------

const LABEL_COL_W = 64;
const HEADER_ROW_H = 18;
const TICK_ROW_H = 18;
const CELL_MARGIN = { top: 4, right: 4, bottom: 4, left: 4 };
const ENLARGED_MARGIN = { top: 24, right: 16, bottom: 32, left: 48 };

// ---------------------------------------------------------------------------
// useZoomPan — DOM-only, zero React re-renders during interaction
// ---------------------------------------------------------------------------

function useZoomPan(
  viewportRef: React.RefObject<HTMLDivElement | null>,
  innerRef: React.RefObject<HTMLDivElement | null>,
  resetBtnRef: React.RefObject<HTMLButtonElement | null>,
) {
  const state = useRef({ scale: 1, tx: 0, ty: 0 });
  const dragging = useRef(false);
  const didDrag = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  const applyTransform = useCallback(() => {
    const { scale, tx, ty } = state.current;
    if (innerRef.current) {
      innerRef.current.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
    }
    if (resetBtnRef.current) {
      resetBtnRef.current.style.display =
        (scale !== 1 || tx !== 0 || ty !== 0) ? "block" : "none";
    }
  }, [innerRef, resetBtnRef]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const s = state.current;
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const factor = e.deltaY > 0 ? 0.92 : 1.08;
      const next = Math.max(0.3, Math.min(4, s.scale * factor));
      const r = next / s.scale;
      s.tx = cx - r * (cx - s.tx);
      s.ty = cy - r * (cy - s.ty);
      s.scale = next;
      applyTransform();
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      dragging.current = true;
      didDrag.current = false;
      last.current = { x: e.clientX, y: e.clientY };
      el.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!dragging.current) return;
      const dx = e.clientX - last.current.x;
      const dy = e.clientY - last.current.y;
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) didDrag.current = true;
      last.current = { x: e.clientX, y: e.clientY };
      state.current.tx += dx;
      state.current.ty += dy;
      applyTransform();
    };

    const onPointerUp = () => { dragging.current = false; };

    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
    };
  }, [viewportRef, applyTransform]);

  const reset = useCallback(() => {
    state.current = { scale: 1, tx: 0, ty: 0 };
    applyTransform();
  }, [applyTransform]);

  const wasDrag = useCallback(() => didDrag.current, []);

  return { reset, wasDrag };
}

// ---------------------------------------------------------------------------
// Memoized sub-components (grid cells — no tooltips for perf)
// ---------------------------------------------------------------------------

const HistogramCellGrid = memo(function HistogramCellGrid({
  values,
  size,
}: {
  values: number[];
  size: number;
  label?: string;
}) {
  const bins = useMemo(() => binData(values), [values]);

  if (bins.length === 0) {
    return (
      <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#6b7280", fontSize: 11 }}>No data</span>
      </div>
    );
  }

  return (
    <div style={{ width: size, height: size, position: "relative" }}>
      <BarChart width={size} height={size} data={bins} margin={CELL_MARGIN}>
        <XAxis dataKey="mid" hide />
        <YAxis hide width={0} />
        <Bar dataKey="count" fill={HISTOGRAM_COLOR} stroke={HISTOGRAM_STROKE} strokeWidth={1} radius={[2, 2, 0, 0]} isAnimationActive={false} />
      </BarChart>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Box plot cell (diagonal)
// ---------------------------------------------------------------------------

const BOX_FILL = "rgba(99, 102, 241, 0.35)";
const BOX_STROKE = "#6366f1";
const MEDIAN_COLOR = "#fbbf24";
const OUTLIER_COLOR = "#f472b6";

const BoxPlotCellGrid = memo(function BoxPlotCellGrid({
  values,
  size,
}: {
  values: number[];
  size: number;
}) {
  const stats = useMemo(() => computeBoxPlotStats(values), [values]);
  if (!stats) {
    return (
      <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#6b7280", fontSize: 11 }}>No data</span>
      </div>
    );
  }

  const m = CELL_MARGIN;
  const plotW = size - m.left - m.right;
  const plotH = size - m.top - m.bottom;
  const { whiskerLow, whiskerHigh, q1, q3, median, outliers } = stats;
  const range = whiskerHigh - whiskerLow || 1;

  const toY = (v: number) => m.top + plotH - ((v - whiskerLow) / range) * plotH;
  const cx = m.left + plotW / 2;
  const boxW = plotW * 0.5;

  return (
    <svg width={size} height={size}>
      {/* Whisker line */}
      <line x1={cx} y1={toY(whiskerLow)} x2={cx} y2={toY(whiskerHigh)} stroke={BOX_STROKE} strokeWidth={1} />
      {/* Whisker caps */}
      <line x1={cx - boxW * 0.3} y1={toY(whiskerLow)} x2={cx + boxW * 0.3} y2={toY(whiskerLow)} stroke={BOX_STROKE} strokeWidth={1} />
      <line x1={cx - boxW * 0.3} y1={toY(whiskerHigh)} x2={cx + boxW * 0.3} y2={toY(whiskerHigh)} stroke={BOX_STROKE} strokeWidth={1} />
      {/* Box */}
      <rect x={cx - boxW / 2} y={toY(q3)} width={boxW} height={toY(q1) - toY(q3)} fill={BOX_FILL} stroke={BOX_STROKE} strokeWidth={1} rx={2} />
      {/* Median */}
      <line x1={cx - boxW / 2} y1={toY(median)} x2={cx + boxW / 2} y2={toY(median)} stroke={MEDIAN_COLOR} strokeWidth={2} />
      {/* Outliers */}
      {outliers.map((v, i) => (
        <circle key={i} cx={cx} cy={toY(v)} r={2} fill={OUTLIER_COLOR} fillOpacity={0.7} />
      ))}
    </svg>
  );
});

// ---------------------------------------------------------------------------
// Violin/KDE cell (diagonal)
// ---------------------------------------------------------------------------

const VIOLIN_FILL = "rgba(99, 102, 241, 0.3)";
const VIOLIN_STROKE = "#818cf8";

const ViolinCellGrid = memo(function ViolinCellGrid({
  values,
  size,
}: {
  values: number[];
  size: number;
}) {
  const kdeData = useMemo(() => gaussianKde(values, 32), [values]);

  if (kdeData.length === 0) {
    return (
      <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#6b7280", fontSize: 11 }}>No data</span>
      </div>
    );
  }

  return (
    <div style={{ width: size, height: size }}>
      <AreaChart width={size} height={size} data={kdeData} margin={CELL_MARGIN}>
        <XAxis dataKey="value" hide />
        <YAxis hide width={0} />
        <Area type="monotone" dataKey="density" fill={VIOLIN_FILL} stroke={VIOLIN_STROKE} strokeWidth={1.5} isAnimationActive={false} />
      </AreaChart>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Line plot cell (off-diagonal)
// ---------------------------------------------------------------------------

const LINE_COLOR = "#818cf8";

const LineCellGrid = memo(function LineCellGrid({
  data,
  size,
  colorByValues,
  groupColorMap,
}: {
  data: { x: number; y: number; idx: number }[];
  size: number;
  colorByValues?: string[];
  groupColorMap?: Map<string, string>;
}) {
  // Split data into separate lines per group
  const groupedLines = useMemo(() => {
    if (!colorByValues || !groupColorMap || groupColorMap.size <= 1) {
      return [{ data: [...data].sort((a, b) => a.x - b.x), color: LINE_COLOR }];
    }
    const byGroup = new Map<string, { x: number; y: number; idx: number }[]>();
    for (const pt of data) {
      const group = colorByValues[pt.idx] ?? "";
      if (!byGroup.has(group)) byGroup.set(group, []);
      byGroup.get(group)!.push(pt);
    }
    return [...byGroup.entries()].map(([key, pts]) => ({
      data: pts.sort((a, b) => a.x - b.x),
      color: groupColorMap.get(key) ?? LINE_COLOR,
    }));
  }, [data, colorByValues, groupColorMap]);

  const domain = useMemo(() => {
    let xMin = Infinity, xMax = -Infinity, yMin = Infinity, yMax = -Infinity;
    for (const pt of data) {
      if (pt.x < xMin) xMin = pt.x;
      if (pt.x > xMax) xMax = pt.x;
      if (pt.y < yMin) yMin = pt.y;
      if (pt.y > yMax) yMax = pt.y;
    }
    return { x: [xMin, xMax] as [number, number], y: [yMin, yMax] as [number, number] };
  }, [data]);

  if (data.length === 0) {
    return (
      <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#6b7280", fontSize: 11 }}>No data</span>
      </div>
    );
  }

  // Render using SVG for multi-line support
  const m = CELL_MARGIN;
  const plotW = size - m.left - m.right;
  const plotH = size - m.top - m.bottom;
  const xSpan = domain.x[1] - domain.x[0] || 1;
  const ySpan = domain.y[1] - domain.y[0] || 1;

  const toX = (v: number) => m.left + ((v - domain.x[0]) / xSpan) * plotW;
  const toY = (v: number) => m.top + plotH - ((v - domain.y[0]) / ySpan) * plotH;

  return (
    <svg width={size} height={size}>
      {groupedLines.map((line, gi) => {
        if (line.data.length < 2) return null;
        const pathD = line.data
          .map((pt, i) => `${i === 0 ? "M" : "L"}${toX(pt.x).toFixed(1)},${toY(pt.y).toFixed(1)}`)
          .join(" ");
        return (
          <path
            key={gi}
            d={pathD}
            fill="none"
            stroke={line.color}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={0.85}
          />
        );
      })}
    </svg>
  );
});

// ---------------------------------------------------------------------------
// Hexbin / heatmap cell (off-diagonal)
// ---------------------------------------------------------------------------

const HEXBIN_PALETTE = [
  "rgba(99, 102, 241, 0.15)",
  "rgba(99, 102, 241, 0.3)",
  "rgba(99, 102, 241, 0.5)",
  "rgba(129, 140, 248, 0.6)",
  "rgba(251, 191, 36, 0.6)",
  "rgba(251, 191, 36, 0.8)",
  "rgba(244, 114, 182, 0.8)",
  "rgba(244, 114, 182, 1)",
];

function hexColor(count: number, maxCount: number): string {
  if (maxCount <= 0) return HEXBIN_PALETTE[0]!;
  const t = count / maxCount;
  const idx = Math.min(Math.floor(t * HEXBIN_PALETTE.length), HEXBIN_PALETTE.length - 1);
  return HEXBIN_PALETTE[idx]!;
}

const HexbinCellGrid = memo(function HexbinCellGrid({
  data,
  size,
}: {
  data: { x: number; y: number; idx: number }[];
  size: number;
}) {
  const hexData = useMemo(() => {
    if (data.length === 0) return { bins: [], maxCount: 0, xRange: [0, 1] as [number, number], yRange: [0, 1] as [number, number] };
    let xMin = Infinity, xMax = -Infinity, yMin = Infinity, yMax = -Infinity;
    for (const d of data) {
      if (d.x < xMin) xMin = d.x;
      if (d.x > xMax) xMax = d.x;
      if (d.y < yMin) yMin = d.y;
      if (d.y > yMax) yMax = d.y;
    }
    const xRange: [number, number] = [xMin, xMax];
    const yRange: [number, number] = [yMin, yMax];
    const gridSize = Math.max(6, Math.min(16, Math.ceil(Math.sqrt(data.length / 3))));
    const bins = hexBin2d(data, xRange, yRange, gridSize);
    const maxCount = Math.max(1, ...bins.map(b => b.count));
    return { bins, maxCount, xRange, yRange };
  }, [data]);

  if (data.length === 0) {
    return (
      <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#6b7280", fontSize: 11 }}>No data</span>
      </div>
    );
  }

  const m = CELL_MARGIN;
  const plotW = size - m.left - m.right;
  const plotH = size - m.top - m.bottom;
  const { bins, maxCount, xRange, yRange } = hexData;
  const xSpan = xRange[1] - xRange[0] || 1;
  const ySpan = yRange[1] - yRange[0] || 1;
  const gridSize = Math.max(6, Math.min(16, Math.ceil(Math.sqrt(data.length / 3))));
  const rectW = Math.max(2, plotW / gridSize);
  const rectH = Math.max(2, plotH / gridSize);

  return (
    <svg width={size} height={size}>
      {bins.map((bin, i) => {
        const px = m.left + ((bin.cx - xRange[0]) / xSpan) * plotW;
        const py = m.top + plotH - ((bin.cy - yRange[0]) / ySpan) * plotH;
        return (
          <rect
            key={i}
            x={px - rectW / 2}
            y={py - rectH / 2}
            width={rectW}
            height={rectH}
            rx={2}
            fill={hexColor(bin.count, maxCount)}
          />
        );
      })}
    </svg>
  );
});

// ---------------------------------------------------------------------------
// 3D Scatter mini-cell — SVG with z encoded as color
//
// Using Plotly WebGL for every grid cell exhausts the browser's WebGL context
// limit (~8-16 contexts). Instead we render a lightweight SVG 2D scatter where
// each point's fill maps the z value to a cool→warm gradient. The full
// interactive 3D Plotly view is reserved for the expanded modal on click.
// ---------------------------------------------------------------------------

const Z_GRADIENT_COOL: [number, number, number] = [59, 130, 246];   // blue-400
const Z_GRADIENT_WARM: [number, number, number] = [239, 68, 68];    // red-400

function zGradientColor(t: number): string {
  const r = Math.round(Z_GRADIENT_COOL[0] + t * (Z_GRADIENT_WARM[0] - Z_GRADIENT_COOL[0]));
  const g = Math.round(Z_GRADIENT_COOL[1] + t * (Z_GRADIENT_WARM[1] - Z_GRADIENT_COOL[1]));
  const b = Math.round(Z_GRADIENT_COOL[2] + t * (Z_GRADIENT_WARM[2] - Z_GRADIENT_COOL[2]));
  return `rgb(${r},${g},${b})`;
}

const Scatter3DCellGrid = memo(function Scatter3DCellGrid({
  data,
  size,
}: {
  data: { x: number; y: number; z: number; idx: number }[];
  size: number;
  colorByValues?: string[];
  colorByNumbers?: number[];
  groupColorMap?: Map<string, string>;
}) {
  const layout = useMemo(() => {
    if (data.length === 0) return null;
    let xMin = Infinity, xMax = -Infinity;
    let yMin = Infinity, yMax = -Infinity;
    let zMin = Infinity, zMax = -Infinity;
    for (const pt of data) {
      if (pt.x < xMin) xMin = pt.x; if (pt.x > xMax) xMax = pt.x;
      if (pt.y < yMin) yMin = pt.y; if (pt.y > yMax) yMax = pt.y;
      if (pt.z < zMin) zMin = pt.z; if (pt.z > zMax) zMax = pt.z;
    }
    return {
      xMin, xMax, xSpan: xMax - xMin || 1,
      yMin, yMax, ySpan: yMax - yMin || 1,
      zMin, zSpan: zMax - zMin || 1,
    };
  }, [data]);

  if (!layout || data.length === 0) {
    return (
      <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#6b7280", fontSize: 11 }}>No data</span>
      </div>
    );
  }

  const m = CELL_MARGIN;
  const plotW = size - m.left - m.right;
  const plotH = size - m.top - m.bottom;
  const r = Math.max(2, Math.min(4, 200 / data.length));

  return (
    <svg width={size} height={size}>
      {data.map((pt) => {
        const cx = m.left + ((pt.x - layout.xMin) / layout.xSpan) * plotW;
        const cy = m.top + plotH - ((pt.y - layout.yMin) / layout.ySpan) * plotH;
        const t = (pt.z - layout.zMin) / layout.zSpan;
        return (
          <circle key={pt.idx} cx={cx} cy={cy} r={r} fill={zGradientColor(t)} opacity={0.8} />
        );
      })}
    </svg>
  );
});

// ---------------------------------------------------------------------------
// 3D Scatter enlarged modal (interactive Plotly)
// ---------------------------------------------------------------------------

function Scatter3DCellModal({
  data, size, r, rPartial, multiR, xLabel, yLabel, zLabel,
  colorByValues, colorByNumbers, groupColorMap,
  onPointClick, selectedRowIdx,
}: {
  data: { x: number; y: number; z: number; idx: number }[];
  size: number;
  r: number;
  rPartial?: number;
  multiR?: number;
  xLabel?: string;
  yLabel?: string;
  zLabel?: string;
  colorByValues?: string[];
  colorByNumbers?: number[];
  groupColorMap?: Map<string, string>;
  onPointClick?: (rowIdx: number) => void;
  selectedRowIdx?: number;
}) {
  const { trace, selectedTrace } = useMemo(() => {
    if (data.length === 0) return { trace: null, selectedTrace: null };
    const colors: string[] = [];
    for (const pt of data) {
      if (groupColorMap && colorByValues) {
        colors.push(groupColorMap.get(colorByValues[pt.idx] ?? "") ?? SCATTER_COLORS[0]!);
      } else if (colorByNumbers) {
        let min = Infinity, max = -Infinity;
        for (const p of data) {
          const v = colorByNumbers[p.idx];
          if (typeof v === "number" && Number.isFinite(v)) { if (v < min) min = v; if (v > max) max = v; }
        }
        const span = max - min || 1;
        const v = colorByNumbers[pt.idx];
        colors.push(typeof v === "number" && Number.isFinite(v) ? gradientColor((v - min) / span) : SCATTER_COLORS[0]!);
      } else {
        colors.push(SCATTER_COLORS[0]!);
      }
    }
    const mainTrace = {
      x: data.map(d => d.x),
      y: data.map(d => d.y),
      z: data.map(d => d.z),
      text: data.map(d => `idx: ${d.idx}`),
      customdata: data.map(d => d.idx),
      type: "scatter3d" as const,
      mode: "markers" as const,
      marker: {
        size: Math.max(2.5, Math.min(5, 300 / data.length)),
        color: colors,
        opacity: 0.7,
      },
      hovertemplate: `${xLabel ?? "x"}: %{x}<br>${yLabel ?? "y"}: %{y}<br>${zLabel ?? "z"}: %{z}<extra></extra>`,
    };
    let selTrace = null;
    if (selectedRowIdx != null) {
      const sel = data.find(d => d.idx === selectedRowIdx);
      if (sel) {
        selTrace = {
          x: [sel.x], y: [sel.y], z: [sel.z],
          type: "scatter3d" as const,
          mode: "markers" as const,
          marker: { size: 10, color: SELECTED_FILL, symbol: "diamond", line: { color: SELECTED_STROKE, width: 2 } },
          showlegend: false,
          hoverinfo: "skip" as const,
        };
      }
    }
    return { trace: mainTrace, selectedTrace: selTrace };
  }, [data, colorByValues, colorByNumbers, groupColorMap, selectedRowIdx, xLabel, yLabel, zLabel]);

  if (!trace || data.length === 0) {
    return (
      <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#6b7280" }}>No data</span>
      </div>
    );
  }

  const layout: Partial<Plotly.Layout> = {
    width: size, height: size,
    paper_bgcolor: "transparent",
    plot_bgcolor: "transparent",
    margin: { t: 30, r: 10, b: 10, l: 10 },
    scene: {
      xaxis: { title: { text: xLabel ?? "" }, gridcolor: "#2a2a4a", backgroundcolor: "transparent" },
      yaxis: { title: { text: yLabel ?? "" }, gridcolor: "#2a2a4a", backgroundcolor: "transparent" },
      zaxis: { title: { text: zLabel ?? "" }, gridcolor: "#2a2a4a", backgroundcolor: "transparent" },
      bgcolor: "transparent",
    },
    showlegend: false,
    font: { color: "#9ca3af", size: 10 },
  };

  const traces = selectedTrace ? [trace, selectedTrace] : [trace];

  return (
    <div style={{ width: size, height: size, position: "relative" }}>
      {/* Correlation stats overlay */}
      <div style={{ position: "absolute", top: 8, right: 16, zIndex: 1, pointerEvents: "none", fontFamily: "var(--font-mono, monospace)", textAlign: "right" }}>
        {Number.isFinite(r) && (
          <div style={{ fontSize: 12, fontWeight: 700, color: rColor(r) }}>
            r = {r.toFixed(3)}
          </div>
        )}
        {rPartial != null && Number.isFinite(rPartial) && (
          <div style={{ fontSize: 11, fontWeight: 600, color: partialRColor(r, rPartial) }}>
            r<sub>partial</sub> = {rPartial.toFixed(3)}
          </div>
        )}
        {multiR != null && Number.isFinite(multiR) && (
          <div style={{ fontSize: 11, fontWeight: 600, color: "#a78bfa" }}>
            R = {multiR.toFixed(3)}
          </div>
        )}
      </div>
      <Plot
        data={traces as unknown as Plotly.Data[]}
        layout={layout as unknown as Plotly.Layout}
        config={{ displayModeBar: true, modeBarButtonsToRemove: ["toImage"], responsive: true }}
        style={{ width: size, height: size }}
        onClick={(event) => {
          if (!onPointClick || !event.points?.[0]) return;
          const pt = event.points[0];
          const idx = (pt as { customdata?: number }).customdata;
          if (typeof idx === "number") onPointClick(idx);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Enlarged modal versions of new plot types
// ---------------------------------------------------------------------------

function BoxPlotCellModal({ values, size, label }: { values: number[]; size: number; label: string }): ReactNode {
  const stats = useMemo(() => computeBoxPlotStats(values), [values]);
  if (!stats) return <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: "#6b7280" }}>No data</span></div>;

  const m = ENLARGED_MARGIN;
  const plotW = size - m.left - m.right;
  const plotH = size - m.top - m.bottom;
  const { whiskerLow, whiskerHigh, q1, q3, median, outliers } = stats;
  const range = (whiskerHigh - whiskerLow) || 1;
  const padded = range * 0.1;
  const lo = whiskerLow - padded;
  const hi = whiskerHigh + padded;
  const fullRange = hi - lo || 1;

  const toY = (v: number) => m.top + plotH - ((v - lo) / fullRange) * plotH;
  const cx = m.left + plotW / 2;
  const boxW = plotW * 0.4;

  return (
    <svg width={size} height={size}>
      <text x={size / 2} y={size - 8} textAnchor="middle" fontSize={12} fill="#9ca3af">{label}</text>
      {/* Whisker */}
      <line x1={cx} y1={toY(whiskerLow)} x2={cx} y2={toY(whiskerHigh)} stroke={BOX_STROKE} strokeWidth={1.5} />
      <line x1={cx - boxW * 0.3} y1={toY(whiskerLow)} x2={cx + boxW * 0.3} y2={toY(whiskerLow)} stroke={BOX_STROKE} strokeWidth={1.5} />
      <line x1={cx - boxW * 0.3} y1={toY(whiskerHigh)} x2={cx + boxW * 0.3} y2={toY(whiskerHigh)} stroke={BOX_STROKE} strokeWidth={1.5} />
      {/* Box */}
      <rect x={cx - boxW / 2} y={toY(q3)} width={boxW} height={Math.max(1, toY(q1) - toY(q3))} fill={BOX_FILL} stroke={BOX_STROKE} strokeWidth={1.5} rx={3} />
      {/* Median */}
      <line x1={cx - boxW / 2} y1={toY(median)} x2={cx + boxW / 2} y2={toY(median)} stroke={MEDIAN_COLOR} strokeWidth={2.5} />
      {/* Stats labels */}
      <text x={cx + boxW / 2 + 8} y={toY(q3) + 4} fontSize={10} fill="#9ca3af">Q3: {shortLabel(q3)}</text>
      <text x={cx + boxW / 2 + 8} y={toY(median) + 4} fontSize={10} fill={MEDIAN_COLOR}>Med: {shortLabel(median)}</text>
      <text x={cx + boxW / 2 + 8} y={toY(q1) + 4} fontSize={10} fill="#9ca3af">Q1: {shortLabel(q1)}</text>
      {/* Outliers */}
      {outliers.map((v, i) => (
        <circle key={i} cx={cx} cy={toY(v)} r={3.5} fill={OUTLIER_COLOR} fillOpacity={0.8} />
      ))}
    </svg>
  );
}

function ViolinCellModal({ values, size, label }: { values: number[]; size: number; label: string }): ReactNode {
  const kdeData = useMemo(() => gaussianKde(values, 64), [values]);
  if (kdeData.length === 0) return <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: "#6b7280" }}>No data</span></div>;

  return (
    <AreaChart width={size} height={size} data={kdeData} margin={{ ...ENLARGED_MARGIN, bottom: 48, left: 56 }}>
      <XAxis dataKey="value" tick={{ fontSize: 10, fill: "#9ca3af" }} tickFormatter={shortLabel} axisLine={{ stroke: "#3a3a5a" }} tickLine={false} label={{ value: label, position: "bottom", offset: 4, style: { fontSize: 12, fill: "#9ca3af" } }} />
      <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={{ stroke: "#3a3a5a" }} tickLine={false} width={40} label={{ value: "Density", angle: -90, position: "insideLeft", offset: -8, style: { fontSize: 12, fill: "#9ca3af" } }} />
      <Tooltip contentStyle={{ background: "#1a1a2e", border: "1px solid #2a2a4a", borderRadius: 6, fontSize: 11, color: "#f0f0f5" }} />
      <Area type="monotone" dataKey="density" fill={VIOLIN_FILL} stroke={VIOLIN_STROKE} strokeWidth={2} />
    </AreaChart>
  );
}

function LineCellModal({
  data, size, xLabel, yLabel, colorByValues, groupColorMap,
}: {
  data: { x: number; y: number; idx: number }[];
  size: number;
  xLabel?: string;
  yLabel?: string;
  colorByValues?: string[];
  groupColorMap?: Map<string, string>;
}): ReactNode {
  const groupedLines = useMemo(() => {
    if (!colorByValues || !groupColorMap || groupColorMap.size <= 1) {
      return [{ key: "all", data: [...data].sort((a, b) => a.x - b.x), color: LINE_COLOR }];
    }
    const byGroup = new Map<string, { x: number; y: number; idx: number }[]>();
    for (const pt of data) {
      const group = colorByValues[pt.idx] ?? "";
      if (!byGroup.has(group)) byGroup.set(group, []);
      byGroup.get(group)!.push(pt);
    }
    return [...byGroup.entries()].map(([key, pts]) => ({
      key,
      data: pts.sort((a, b) => a.x - b.x),
      color: groupColorMap.get(key) ?? LINE_COLOR,
    }));
  }, [data, colorByValues, groupColorMap]);

  if (data.length === 0) return <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: "#6b7280" }}>No data</span></div>;

  // Compute global domain
  const domain = useMemo(() => {
    let xMin = Infinity, xMax = -Infinity, yMin = Infinity, yMax = -Infinity;
    for (const pt of data) {
      if (pt.x < xMin) xMin = pt.x;
      if (pt.x > xMax) xMax = pt.x;
      if (pt.y < yMin) yMin = pt.y;
      if (pt.y > yMax) yMax = pt.y;
    }
    return { x: [xMin, xMax] as [number, number], y: [yMin, yMax] as [number, number] };
  }, [data]);

  const m = xLabel ? { ...ENLARGED_MARGIN, bottom: 48, left: 56 } : ENLARGED_MARGIN;
  const plotW = size - m.left - m.right;
  const plotH = size - m.top - m.bottom;
  const xSpan = domain.x[1] - domain.x[0] || 1;
  const ySpan = domain.y[1] - domain.y[0] || 1;

  const toX = (v: number) => m.left + ((v - domain.x[0]) / xSpan) * plotW;
  const toY = (v: number) => m.top + plotH - ((v - domain.y[0]) / ySpan) * plotH;

  // Tick values
  const xTicks = [domain.x[0], domain.x[0] + xSpan * 0.25, domain.x[0] + xSpan * 0.5, domain.x[0] + xSpan * 0.75, domain.x[1]];
  const yTicks = [domain.y[0], domain.y[0] + ySpan * 0.25, domain.y[0] + ySpan * 0.5, domain.y[0] + ySpan * 0.75, domain.y[1]];

  return (
    <svg width={size} height={size}>
      {/* Axes */}
      <line x1={m.left} y1={m.top + plotH} x2={m.left + plotW} y2={m.top + plotH} stroke="#3a3a5a" />
      <line x1={m.left} y1={m.top} x2={m.left} y2={m.top + plotH} stroke="#3a3a5a" />
      {xTicks.map((v, i) => (
        <text key={`xt-${i}`} x={toX(v)} y={m.top + plotH + 14} textAnchor="middle" fontSize={10} fill="#9ca3af">{shortLabel(v)}</text>
      ))}
      {yTicks.map((v, i) => (
        <text key={`yt-${i}`} x={m.left - 4} y={toY(v) + 3} textAnchor="end" fontSize={10} fill="#9ca3af">{shortLabel(v)}</text>
      ))}
      {xLabel && <text x={m.left + plotW / 2} y={size - 8} textAnchor="middle" fontSize={12} fill="#9ca3af">{xLabel}</text>}
      {yLabel && <text x={14} y={m.top + plotH / 2} textAnchor="middle" fontSize={12} fill="#9ca3af" transform={`rotate(-90, 14, ${m.top + plotH / 2})`}>{yLabel}</text>}
      {/* Lines per group */}
      {groupedLines.map((line) => {
        if (line.data.length < 2) return null;
        const pathD = line.data.map((pt, i) => `${i === 0 ? "M" : "L"}${toX(pt.x).toFixed(1)},${toY(pt.y).toFixed(1)}`).join(" ");
        return (
          <g key={line.key}>
            <path d={pathD} fill="none" stroke={line.color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" opacity={0.9} />
            {line.data.map((pt, i) => (
              <circle key={i} cx={toX(pt.x)} cy={toY(pt.y)} r={2.5} fill={line.color} fillOpacity={0.8} />
            ))}
          </g>
        );
      })}
    </svg>
  );
}

function HexbinCellModal({ data, size, xLabel, yLabel }: { data: { x: number; y: number; idx: number }[]; size: number; xLabel?: string; yLabel?: string }): ReactNode {
  const hexData = useMemo(() => {
    if (data.length === 0) return { bins: [], maxCount: 0, xRange: [0, 1] as [number, number], yRange: [0, 1] as [number, number] };
    let xMin = Infinity, xMax = -Infinity, yMin = Infinity, yMax = -Infinity;
    for (const d of data) {
      if (d.x < xMin) xMin = d.x;
      if (d.x > xMax) xMax = d.x;
      if (d.y < yMin) yMin = d.y;
      if (d.y > yMax) yMax = d.y;
    }
    const gridSize = Math.max(8, Math.min(24, Math.ceil(Math.sqrt(data.length / 2))));
    const bins = hexBin2d(data, [xMin, xMax], [yMin, yMax], gridSize);
    const maxCount = Math.max(1, ...bins.map(b => b.count));
    return { bins, maxCount, xRange: [xMin, xMax] as [number, number], yRange: [yMin, yMax] as [number, number] };
  }, [data]);

  if (data.length === 0) return <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: "#6b7280" }}>No data</span></div>;

  const m = ENLARGED_MARGIN;
  const plotW = size - m.left - m.right;
  const plotH = size - m.top - m.bottom;
  const { bins, maxCount, xRange, yRange } = hexData;
  const xSpan = xRange[1] - xRange[0] || 1;
  const ySpan = yRange[1] - yRange[0] || 1;
  const gridSize = Math.max(8, Math.min(24, Math.ceil(Math.sqrt(data.length / 2))));
  const rW = Math.max(4, plotW / gridSize);
  const rH = Math.max(4, plotH / gridSize);

  return (
    <svg width={size} height={size}>
      {/* Axis labels */}
      {xLabel && <text x={m.left + plotW / 2} y={size - 8} textAnchor="middle" fontSize={12} fill="#9ca3af">{xLabel}</text>}
      {yLabel && <text x={14} y={m.top + plotH / 2} textAnchor="middle" fontSize={12} fill="#9ca3af" transform={`rotate(-90, 14, ${m.top + plotH / 2})`}>{yLabel}</text>}
      {/* Axis ticks */}
      <line x1={m.left} y1={m.top + plotH} x2={m.left + plotW} y2={m.top + plotH} stroke="#3a3a5a" />
      <line x1={m.left} y1={m.top} x2={m.left} y2={m.top + plotH} stroke="#3a3a5a" />
      <text x={m.left} y={m.top + plotH + 14} fontSize={10} fill="#9ca3af">{shortLabel(xRange[0])}</text>
      <text x={m.left + plotW} y={m.top + plotH + 14} fontSize={10} fill="#9ca3af" textAnchor="end">{shortLabel(xRange[1])}</text>
      <text x={m.left - 4} y={m.top + plotH} fontSize={10} fill="#9ca3af" textAnchor="end">{shortLabel(yRange[0])}</text>
      <text x={m.left - 4} y={m.top + 10} fontSize={10} fill="#9ca3af" textAnchor="end">{shortLabel(yRange[1])}</text>
      {/* Bins */}
      {bins.map((bin, i) => {
        const px = m.left + ((bin.cx - xRange[0]) / xSpan) * plotW;
        const py = m.top + plotH - ((bin.cy - yRange[0]) / ySpan) * plotH;
        return (
          <rect key={i} x={px - rW / 2} y={py - rH / 2} width={rW} height={rH} rx={3} fill={hexColor(bin.count, maxCount)}>
            <title>{bin.count} points</title>
          </rect>
        );
      })}
    </svg>
  );
}

const SELECTED_FILL = "#fbbf24";
const SELECTED_STROKE = "#f59e0b";
const SEL_DOT_SIZE = 12;

const ScatterCellGrid = memo(function ScatterCellGrid({
  data,
  size,
  colorByValues,
  colorByNumbers,
  onPointClick,
  onHoverPoint,
  groupColorMap,
}: {
  data: { x: number; y: number; idx: number }[];
  size: number;
  r?: number;
  colorByValues?: string[];
  colorByNumbers?: number[];
  onPointClick?: (rowIdx: number) => void;
  onHoverPoint?: React.RefObject<HoverInfo | null>;
  groupColorMap?: Map<string, string>;
}) {
  const categoryColors = useMemo(() => {
    if (groupColorMap) return groupColorMap;
    if (!colorByValues) return null;
    const unique = Array.from(new Set(colorByValues));
    const map = new Map<string, string>();
    unique.forEach((cat, i) => map.set(cat, SCATTER_COLORS[i % SCATTER_COLORS.length]!));
    return map;
  }, [colorByValues, groupColorMap]);

  const numRange = useMemo(() => {
    if (!colorByNumbers) return null;
    let min = Infinity, max = -Infinity;
    for (const pt of data) {
      const v = colorByNumbers[pt.idx];
      if (typeof v === "number" && Number.isFinite(v)) {
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }
    const span = max - min;
    return { min, span: span > 0 ? span : 1 };
  }, [colorByNumbers, data]);

  const handleChartMouseMove = useCallback((state: Record<string, unknown>) => {
    if (!onHoverPoint) return;
    const payload = (state as { activePayload?: { payload?: { x: number; y: number; idx: number } }[] }).activePayload;
    if (payload && payload.length > 0 && payload[0]?.payload) {
      const cur = onHoverPoint.current;
      if (cur) cur.point = payload[0].payload;
    }
  }, [onHoverPoint]);

  const handleChartMouseLeave = useCallback(() => {
    if (onHoverPoint?.current) onHoverPoint.current.point = undefined;
  }, [onHoverPoint]);

  if (data.length === 0) {
    return (
      <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#6b7280", fontSize: 11 }}>No data</span>
      </div>
    );
  }

  const pointColor = (idx: number): string => {
    if (colorByNumbers && numRange) {
      const v = colorByNumbers[idx];
      if (typeof v === "number" && Number.isFinite(v)) {
        return gradientColor((v - numRange.min) / numRange.span);
      }
    }
    if (categoryColors && colorByValues) {
      return categoryColors.get(colorByValues[idx] ?? "") ?? SCATTER_COLORS[0]!;
    }
    return SCATTER_COLORS[0]!;
  };

  const pointRadius = Math.max(2, Math.min(4, 200 / data.length));

  return (
    <div style={{ width: size, height: size }}>
      <ScatterChart
        width={size}
        height={size}
        margin={CELL_MARGIN}
        onMouseMove={handleChartMouseMove}
        onMouseLeave={handleChartMouseLeave}
      >
        <XAxis dataKey="x" type="number" hide domain={["dataMin", "dataMax"]} />
        <YAxis dataKey="y" type="number" hide width={0} domain={["dataMin", "dataMax"]} />
        <Tooltip content={() => null} />
        <Scatter
          data={data}
          isAnimationActive={false}
          onClick={(point: Record<string, unknown>, _index: number, e: React.MouseEvent) => {
            if (!onPointClick) return;
            e.stopPropagation();
            const idx = (point as { idx?: number }).idx;
            if (typeof idx === "number") onPointClick(idx);
          }}
          cursor="pointer"
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={pointColor(entry.idx)}
              fillOpacity={0.7}
              r={pointRadius}
            />
          ))}
        </Scatter>
      </ScatterChart>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Canvas-based scatter cell — replaces SVG for large datasets (N > 200)
// ---------------------------------------------------------------------------

const LTTB_GRID_TARGET = 350; // points per grid cell after downsampling

const CanvasScatterCell = memo(function CanvasScatterCell({
  data,
  size,
  row,
  col,
  colorByValues,
  colorByNumbers,
  onPointClick,
  onHoverPoint,
  groupColorMap,
}: {
  data: { x: number; y: number; idx: number }[];
  size: number;
  row: number;
  col: number;
  colorByValues?: string[];
  colorByNumbers?: number[];
  onPointClick?: (rowIdx: number) => void;
  onHoverPoint?: React.RefObject<HoverInfo | null>;
  groupColorMap?: Map<string, string>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Downsample for the grid cell view
  const displayData = useMemo(() => {
    if (data.length <= 500) return data;
    return lttbDownsample(data, LTTB_GRID_TARGET);
  }, [data]);

  const categoryColors = useMemo(() => {
    if (groupColorMap) return groupColorMap;
    if (!colorByValues) return null;
    const unique = Array.from(new Set(colorByValues));
    const map = new Map<string, string>();
    unique.forEach((cat, i) => map.set(cat, SCATTER_COLORS[i % SCATTER_COLORS.length]!));
    return map;
  }, [colorByValues, groupColorMap]);

  const numRange = useMemo(() => {
    if (!colorByNumbers) return null;
    let min = Infinity, max = -Infinity;
    for (const pt of data) {
      const v = colorByNumbers[pt.idx];
      if (typeof v === "number" && Number.isFinite(v)) {
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }
    const span = max - min;
    return { min, span: span > 0 ? span : 1 };
  }, [colorByNumbers, data]);

  const pointColor = useCallback((idx: number): string => {
    if (colorByNumbers && numRange) {
      const v = colorByNumbers[idx];
      if (typeof v === "number" && Number.isFinite(v)) {
        return gradientColor((v - numRange.min) / numRange.span);
      }
    }
    if (categoryColors && colorByValues) {
      return categoryColors.get(colorByValues[idx] ?? "") ?? SCATTER_COLORS[0]!;
    }
    return SCATTER_COLORS[0]!;
  }, [colorByNumbers, numRange, categoryColors, colorByValues]);

  // Domain for coordinate mapping
  const domain = useMemo(() => {
    if (data.length === 0) return null;
    let xMin = Infinity, xMax = -Infinity, yMin = Infinity, yMax = -Infinity;
    for (const d of data) {
      if (d.x < xMin) xMin = d.x;
      if (d.x > xMax) xMax = d.x;
      if (d.y < yMin) yMin = d.y;
      if (d.y > yMax) yMax = d.y;
    }
    return {
      xMin, xMax, xSpan: xMax - xMin || 1,
      yMin, yMax, ySpan: yMax - yMin || 1,
    };
  }, [data]);

  // Draw on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !domain || displayData.length === 0) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, size, size);

    const m = CELL_MARGIN;
    const plotW = size - m.left - m.right;
    const plotH = size - m.top - m.bottom;
    const r = Math.max(2, Math.min(4, 200 / displayData.length));

    // Group points by color for batched drawing
    const colorGroups = new Map<string, { x: number; y: number }[]>();
    for (const pt of displayData) {
      const c = pointColor(pt.idx);
      if (!colorGroups.has(c)) colorGroups.set(c, []);
      const px = m.left + ((pt.x - domain.xMin) / domain.xSpan) * plotW;
      const py = m.top + plotH - ((pt.y - domain.yMin) / domain.ySpan) * plotH;
      colorGroups.get(c)!.push({ x: px, y: py });
    }

    ctx.globalAlpha = 0.7;
    for (const [color, points] of colorGroups) {
      ctx.fillStyle = color;
      ctx.beginPath();
      for (const p of points) {
        ctx.moveTo(p.x + r, p.y);
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      }
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }, [displayData, size, domain, pointColor]);

  // Click handler — find nearest point within the rendered dot's radius.
  // Only consume the click if the cursor is essentially *on* a dot; otherwise
  // let it bubble so the parent cell's onClick opens the enlarged modal.
  const handleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onPointClick || !domain) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    const m = CELL_MARGIN;
    const plotW = size - m.left - m.right;
    const plotH = size - m.top - m.bottom;

    // Hit radius must be tight (= rendered dot size + 1px tolerance),
    // otherwise every click in a dense cell lands "near" a point and
    // blocks the bubble-up that opens the enlarged modal view.
    const pointRadius = Math.max(2, Math.min(4, 200 / Math.max(displayData.length, 1)));
    const hitRadius = pointRadius + 1;
    let bestDist = Infinity;
    let bestIdx = -1;

    // Iterate the displayed (downsampled) points — those are the dots the
    // user actually sees. Clicking something not rendered shouldn't count.
    for (const pt of displayData) {
      const px = m.left + ((pt.x - domain.xMin) / domain.xSpan) * plotW;
      const py = m.top + plotH - ((pt.y - domain.yMin) / domain.ySpan) * plotH;
      const dx = mx - px;
      const dy = my - py;
      const dist = dx * dx + dy * dy;
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = pt.idx;
      }
    }

    if (bestIdx >= 0 && bestDist <= hitRadius * hitRadius) {
      e.stopPropagation();
      onPointClick(bestIdx);
    }
  }, [onPointClick, displayData, domain, size]);

  // Mouse move handler for hover info
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onHoverPoint) return;
    if (!domain) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    const m = CELL_MARGIN;
    const plotW = size - m.left - m.right;
    const plotH = size - m.top - m.bottom;

    let bestDist = Infinity;
    let bestPt: { x: number; y: number; idx: number } | undefined;
    const hitRadius = 12;

    for (const pt of displayData) {
      const px = m.left + ((pt.x - domain.xMin) / domain.xSpan) * plotW;
      const py = m.top + plotH - ((pt.y - domain.yMin) / domain.ySpan) * plotH;
      const dx = mx - px;
      const dy = my - py;
      const dist = dx * dx + dy * dy;
      if (dist < bestDist) {
        bestDist = dist;
        bestPt = pt;
      }
    }

    if (bestPt && bestDist <= hitRadius * hitRadius) {
      if (!onHoverPoint.current) onHoverPoint.current = { row, col };
      onHoverPoint.current.row = row;
      onHoverPoint.current.col = col;
      onHoverPoint.current.point = bestPt;
    } else if (onHoverPoint.current) {
      onHoverPoint.current.point = undefined;
    }
  }, [onHoverPoint, displayData, domain, size, row, col]);

  const handleMouseLeave = useCallback(() => {
    if (onHoverPoint?.current) onHoverPoint.current.point = undefined;
  }, [onHoverPoint]);

  if (data.length === 0) {
    return (
      <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#6b7280", fontSize: 11 }}>No data</span>
      </div>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{ width: size, height: size, cursor: "pointer" }}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    />
  );
});

// ---------------------------------------------------------------------------
// Lazy cell wrapper — only renders chart when visible in viewport
// ---------------------------------------------------------------------------

const LAZY_THRESHOLD = 36; // minimum number of cells before enabling lazy rendering

function LazyCell({
  children,
  size,
  placeholder,
}: {
  children: ReactNode;
  size: number;
  placeholder?: ReactNode;
}) {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        }
      },
      { rootMargin: "100px" }, // preload cells 100px before they scroll into view
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} style={{ width: size, height: size }}>
      {isVisible ? children : (placeholder ?? <div style={{ width: size, height: size, background: "var(--gray-a2, rgba(99,102,241,0.02))" }} />)}
    </div>
  );
}

/**
 * Pure HTML overlay dot for the selected point. Renders on top of the
 * Recharts chart without causing the chart to re-render.
 */
function SelectionDot({
  data,
  size,
  selectedRowIdx,
}: {
  data: { x: number; y: number; idx: number }[];
  size: number;
  selectedRowIdx: number;
}): ReactNode {
  const pt = data.find(d => d.idx === selectedRowIdx);
  if (!pt || data.length < 2) return null;

  let xMin = Infinity, xMax = -Infinity, yMin = Infinity, yMax = -Infinity;
  for (const d of data) {
    if (d.x < xMin) xMin = d.x;
    if (d.x > xMax) xMax = d.x;
    if (d.y < yMin) yMin = d.y;
    if (d.y > yMax) yMax = d.y;
  }
  const xSpan = xMax - xMin || 1;
  const ySpan = yMax - yMin || 1;

  const chartW = size - CELL_MARGIN.left - CELL_MARGIN.right;
  const chartH = size - CELL_MARGIN.top - CELL_MARGIN.bottom;
  const px = CELL_MARGIN.left + ((pt.x - xMin) / xSpan) * chartW;
  const py = CELL_MARGIN.top + (1 - (pt.y - yMin) / ySpan) * chartH;
  const half = SEL_DOT_SIZE / 2;

  return (
    <div style={{
      position: "absolute",
      left: px - half, top: py - half,
      width: SEL_DOT_SIZE, height: SEL_DOT_SIZE,
      borderRadius: "50%",
      background: SELECTED_FILL,
      border: `2px solid ${SELECTED_STROKE}`,
      pointerEvents: "none",
      zIndex: 2,
      boxShadow: "0 0 6px rgba(251, 191, 36, 0.6)",
    }} />
  );
}

// ---------------------------------------------------------------------------
// Surface quality badge — compact overlay on 3D grid cells
// ---------------------------------------------------------------------------

/** Color for surface quality: high = vibrant, low = muted. */
function surfaceQualityColor(q: number): string {
  if (q > 0.6) return "#34d399";  // strong surface
  if (q > 0.4) return "#fbbf24";  // moderate
  if (q > 0.2) return "#60a5fa";  // weak
  return "#4b5563";               // minimal
}

const SurfaceQualityBadge = memo(function SurfaceQualityBadge({
  surfaceQuality,
  rPartial,
  r,
  cellSize,
}: {
  surfaceQuality: number;
  rPartial?: number;
  r: number;
  cellSize: number;
}) {
  const q = surfaceQuality;
  const color = surfaceQualityColor(q);
  const showPartial = rPartial != null && Number.isFinite(rPartial) && cellSize >= 70;

  return (
    <div style={{
      position: "absolute", bottom: 0, left: 0, right: 0,
      display: "flex", justifyContent: "space-between", alignItems: "flex-end",
      padding: "0 2px 1px",
      pointerEvents: "none",
    }}>
      {/* Surface quality indicator: small colored bar */}
      <div style={{
        height: 2, borderRadius: 1,
        width: `${Math.max(15, q * 100)}%`,
        background: color,
        opacity: 0.8,
      }} />
      {showPartial && (
        <div style={{
          fontSize: 7, fontWeight: 700, fontFamily: "var(--font-mono, monospace)",
          color: partialRColor(r, rPartial!),
          lineHeight: 1,
        }}>
          r<sub>p</sub>={rPartial!.toFixed(2)}
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Full-featured cells for the enlarged modal (with axes + tooltips)
// ---------------------------------------------------------------------------

function HistogramCellModal({ values, size, label, showAxisLabels }: { values: number[]; size: number; label: string; showAxisLabels?: boolean }): ReactNode {
  const bins = useMemo(() => binData(values), [values]);
  if (bins.length === 0) return <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: "#6b7280" }}>No data</span></div>;
  return (
    <BarChart width={size} height={size} data={bins} margin={showAxisLabels ? { ...ENLARGED_MARGIN, bottom: 48, left: 56 } : ENLARGED_MARGIN}>
      <XAxis dataKey="mid" tick={{ fontSize: 10, fill: "#9ca3af" }} tickFormatter={shortLabel} axisLine={{ stroke: "#3a3a5a" }} tickLine={false} label={showAxisLabels ? { value: label, position: "bottom", offset: 4, style: { fontSize: 12, fill: "var(--gray-11, #9ca3af)" } } : undefined} />
      <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} axisLine={{ stroke: "#3a3a5a" }} tickLine={false} width={40} label={showAxisLabels ? { value: "Count", angle: -90, position: "insideLeft", offset: -8, style: { fontSize: 12, fill: "var(--gray-11, #9ca3af)" } } : undefined} />
      <Tooltip contentStyle={{ background: "#1a1a2e", border: "1px solid #2a2a4a", borderRadius: 6, fontSize: 11, color: "#f0f0f5" }} formatter={(v) => [v ?? 0, "Count"]} labelFormatter={(l) => shortLabel(Number(l))} />
      <Bar dataKey="count" fill={HISTOGRAM_COLOR} stroke={HISTOGRAM_STROKE} strokeWidth={1} radius={[2, 2, 0, 0]} />
    </BarChart>
  );
}

/** Threshold above which the modal uses Plotly scattergl (WebGL) instead of Recharts SVG. */
const MODAL_WEBGL_THRESHOLD = 500;

function ScatterCellModal({ data, size, r: corr, colorByValues, colorByNumbers, onPointClick, selectedRowIdx, xLabel, yLabel, groupColorMap }: {
  data: { x: number; y: number; idx: number }[];
  size: number;
  r: number;
  colorByValues?: string[];
  colorByNumbers?: number[];
  onPointClick?: (rowIdx: number) => void;
  selectedRowIdx?: number;
  xLabel?: string;
  yLabel?: string;
  groupColorMap?: Map<string, string>;
}): ReactNode {
  const categoryColors = useMemo(() => {
    if (groupColorMap) return groupColorMap;
    if (!colorByValues) return null;
    const unique = Array.from(new Set(colorByValues));
    const map = new Map<string, string>();
    unique.forEach((cat, i) => map.set(cat, SCATTER_COLORS[i % SCATTER_COLORS.length]!));
    return map;
  }, [colorByValues, groupColorMap]);
  const numRange = useMemo(() => {
    if (!colorByNumbers) return null;
    let min = Infinity, max = -Infinity;
    for (const pt of data) { const v = colorByNumbers[pt.idx]; if (typeof v === "number" && Number.isFinite(v)) { if (v < min) min = v; if (v > max) max = v; } }
    const span = max - min;
    return { min, span: span > 0 ? span : 1 };
  }, [colorByNumbers, data]);
  const pointColor = useCallback((idx: number): string => {
    if (colorByNumbers && numRange) { const v = colorByNumbers[idx]; if (typeof v === "number" && Number.isFinite(v)) return gradientColor((v - numRange.min) / numRange.span); }
    if (categoryColors && colorByValues) return categoryColors.get(colorByValues[idx] ?? "") ?? SCATTER_COLORS[0]!;
    return SCATTER_COLORS[0]!;
  }, [colorByNumbers, numRange, categoryColors, colorByValues]);

  if (data.length === 0) return <div style={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: "#6b7280" }}>No data</span></div>;

  // For large N, use Plotly with scattergl (WebGL-accelerated)
  if (data.length > MODAL_WEBGL_THRESHOLD) {
    return (
      <ScatterCellModalPlotly
        data={data}
        size={size}
        r={corr}
        pointColor={pointColor}
        onPointClick={onPointClick}
        selectedRowIdx={selectedRowIdx}
        xLabel={xLabel}
        yLabel={yLabel}
      />
    );
  }

  // Small dataset — keep Recharts SVG for rich interactivity
  const pr = Math.max(3, Math.min(6, 400 / data.length));
  const selPr = Math.max(pr * 2, 8);

  const sortedData = [...data].sort((a, b) =>
    (a.idx === selectedRowIdx ? 1 : 0) - (b.idx === selectedRowIdx ? 1 : 0),
  );

  return (
    <div style={{ width: size, height: size, position: "relative" }}>
      {Number.isFinite(corr) && (
        <div style={{ position: "absolute", top: 8, right: 16, fontSize: 13, fontWeight: 700, color: rColor(corr), zIndex: 1, pointerEvents: "none", fontFamily: "var(--font-mono, monospace)" }}>
          r = {corr.toFixed(3)}
        </div>
      )}
      <ScatterChart width={size} height={size} margin={xLabel ? { ...ENLARGED_MARGIN, bottom: 48, left: 56 } : ENLARGED_MARGIN}>
        <XAxis dataKey="x" type="number" tick={{ fontSize: 10, fill: "#9ca3af" }} tickFormatter={shortLabel} axisLine={{ stroke: "#3a3a5a" }} tickLine={false} domain={["dataMin", "dataMax"]} label={xLabel ? { value: xLabel, position: "bottom", offset: 4, style: { fontSize: 12, fill: "var(--gray-11, #9ca3af)" } } : undefined} />
        <YAxis dataKey="y" type="number" tick={{ fontSize: 10, fill: "#9ca3af" }} tickFormatter={shortLabel} axisLine={{ stroke: "#3a3a5a" }} tickLine={false} width={40} domain={["dataMin", "dataMax"]} label={yLabel ? { value: yLabel, angle: -90, position: "insideLeft", offset: -8, style: { fontSize: 12, fill: "var(--gray-11, #9ca3af)" } } : undefined} />
        <Tooltip contentStyle={{ background: "#1a1a2e", border: "1px solid #2a2a4a", borderRadius: 6, fontSize: 11, color: "#f0f0f5" }} formatter={(v) => shortLabel(Number(v ?? 0))} />
        <Scatter
          data={sortedData}
          isAnimationActive={false}
          onClick={(point: Record<string, unknown>) => {
            const idx = (point as { idx?: number }).idx;
            if (typeof idx === "number" && onPointClick) onPointClick(idx);
          }}
          cursor="pointer"
        >
          {sortedData.map((entry, index) => {
            const sel = entry.idx === selectedRowIdx;
            return <Cell key={`c-${index}`} fill={sel ? SELECTED_FILL : pointColor(entry.idx)} stroke={sel ? SELECTED_STROKE : "none"} strokeWidth={sel ? 2 : 0} fillOpacity={sel ? 1 : 0.7} r={sel ? selPr : pr} />;
          })}
        </Scatter>
      </ScatterChart>
    </div>
  );
}

/**
 * Plotly-based modal scatter using scattergl (WebGL) for large datasets.
 * Shows ALL points (no downsampling) since it's a single chart with WebGL.
 */
function ScatterCellModalPlotly({ data, size, r: corr, pointColor, onPointClick, selectedRowIdx, xLabel, yLabel }: {
  data: { x: number; y: number; idx: number }[];
  size: number;
  r: number;
  pointColor: (idx: number) => string;
  onPointClick?: (rowIdx: number) => void;
  selectedRowIdx?: number;
  xLabel?: string;
  yLabel?: string;
}): ReactNode {
  const { traces, layout } = useMemo(() => {
    const colors = data.map(pt => pointColor(pt.idx));
    const markerSize = Math.max(2.5, Math.min(5, 400 / data.length));

    const mainTrace: Partial<Plotly.PlotData> = {
      x: data.map(d => d.x),
      y: data.map(d => d.y),
      customdata: data.map(d => d.idx) as unknown as Plotly.Datum[],
      type: "scattergl" as Plotly.PlotType,
      mode: "markers",
      marker: {
        size: markerSize,
        color: colors,
        opacity: 0.7,
      },
      hovertemplate: `${xLabel ?? "x"}: %{x}<br>${yLabel ?? "y"}: %{y}<extra></extra>`,
    };

    const allTraces: Partial<Plotly.PlotData>[] = [mainTrace];

    // Highlighted selected point
    if (selectedRowIdx != null) {
      const sel = data.find(d => d.idx === selectedRowIdx);
      if (sel) {
        allTraces.push({
          x: [sel.x],
          y: [sel.y],
          type: "scattergl" as Plotly.PlotType,
          mode: "markers",
          marker: {
            size: 12,
            color: SELECTED_FILL,
            line: { color: SELECTED_STROKE, width: 2 },
          },
          showlegend: false,
          hoverinfo: "skip",
        } as Partial<Plotly.PlotData>);
      }
    }

    const plotLayout: Partial<Plotly.Layout> = {
      width: size,
      height: size,
      paper_bgcolor: "transparent",
      plot_bgcolor: "transparent",
      margin: { t: 30, r: 16, b: xLabel ? 52 : 32, l: yLabel ? 56 : 48 },
      xaxis: {
        title: xLabel ? { text: xLabel, font: { size: 12, color: "#9ca3af" } } : undefined,
        gridcolor: "#2a2a4a",
        zerolinecolor: "#3a3a5a",
        tickfont: { size: 10, color: "#9ca3af" },
      },
      yaxis: {
        title: yLabel ? { text: yLabel, font: { size: 12, color: "#9ca3af" } } : undefined,
        gridcolor: "#2a2a4a",
        zerolinecolor: "#3a3a5a",
        tickfont: { size: 10, color: "#9ca3af" },
      },
      showlegend: false,
      font: { color: "#9ca3af" },
    };

    return { traces: allTraces, layout: plotLayout };
  }, [data, pointColor, selectedRowIdx, xLabel, yLabel, size]);

  return (
    <div style={{ width: size, height: size, position: "relative" }}>
      {Number.isFinite(corr) && (
        <div style={{ position: "absolute", top: 8, right: 16, fontSize: 13, fontWeight: 700, color: rColor(corr), zIndex: 1, pointerEvents: "none", fontFamily: "var(--font-mono, monospace)" }}>
          r = {corr.toFixed(3)}
        </div>
      )}
      <Plot
        data={traces as Plotly.Data[]}
        layout={layout as Plotly.Layout}
        config={{ displayModeBar: true, modeBarButtonsToRemove: ["toImage" as Plotly.ModeBarDefaultButtons], responsive: true }}
        style={{ width: size, height: size }}
        onClick={(event) => {
          if (!onPointClick || !event.points?.[0]) return;
          const pt = event.points[0];
          const idx = (pt as { customdata?: number }).customdata;
          if (typeof idx === "number") onPointClick(idx);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tick label cell (plain HTML)
// ---------------------------------------------------------------------------

const TICK_STYLE: React.CSSProperties = {
  fontSize: 8, color: "var(--gray-9, #6b7280)", fontFamily: "var(--font-mono, monospace)",
  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
};

function BottomTickCell({ range, size }: { range: ColRange | null; size: number }): ReactNode {
  if (!range) return <div style={{ width: size }} />;
  return (
    <div style={{ width: size, height: TICK_ROW_H, display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "2px 2px 0" }}>
      <span style={TICK_STYLE}>{shortLabel(range.min)}</span>
      <span style={TICK_STYLE}>{shortLabel(range.max)}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Expanded cell overlay (inline, fills 90% of grid area)
// ---------------------------------------------------------------------------

function ExpandedCellOverlay({
  row,
  col,
  numericColumns,
  matrixData,
  containerSize,
  colorByValues,
  colorByNumbers,
  groupColorMap,
  zAxisKey,
  onClose,
  onPointClick,
  selectedRowIdx,
  offDiagPlotType = "scatter",
  diagPlotType = "histogram",
}: {
  row: number;
  col: number;
  numericColumns: CorrelogramColumn[];
  matrixData: Record<string, { pairs?: { x: number; y: number; idx: number }[]; triples?: { x: number; y: number; z: number; idx: number }[]; values?: number[]; r?: number; rPartial?: number; multiR?: number; surfaceQuality?: number }>;
  containerSize: { width: number; height: number };
  colorByValues?: string[];
  colorByNumbers?: number[];
  groupColorMap?: Map<string, string>;
  zAxisKey?: string;
  onClose: () => void;
  onPointClick?: (rowIdx: number) => void;
  selectedRowIdx?: number;
  offDiagPlotType?: OffDiagPlotType;
  diagPlotType?: DiagPlotType;
}): ReactNode {
  const cellData = matrixData[`${row}-${col}`];
  const rowCol = numericColumns[row]!;
  const colCol = numericColumns[col]!;
  const isDiagonal = row === col;
  const is3D = offDiagPlotType === "scatter3d" && !isDiagonal && !!cellData?.triples;
  const title = isDiagonal
    ? rowCol.header
    : is3D
    ? `${colCol.header} vs ${rowCol.header} vs ${zAxisKey ?? "Z"}`
    : `${colCol.header} vs ${rowCol.header}`;

  const chartSize = Math.floor(Math.min(containerSize.width - INFO_PANEL_W - 80, containerSize.height - 100) * 0.9);

  const { openPlotBuilder } = useProvenanceContext();

  const handleOpenInBuilder = () => {
    if (!isDiagonal) {
      openPlotBuilder(colCol.key, rowCol.key, is3D ? zAxisKey : undefined);
    }
    onClose();
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "absolute", inset: 0, zIndex: 20,
        background: "rgba(0,0,0,0.6)", backdropFilter: "blur(3px)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "var(--color-surface, #1a1a2e)",
          border: "1px solid var(--gray-a5, #2a2a4a)",
          borderRadius: 12, padding: 16,
          display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
          boxShadow: "0 12px 40px rgba(0,0,0,.5)",
          maxWidth: "92%", maxHeight: "92%",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--gray-12, #f0f0f5)", whiteSpace: "nowrap" }}>{title}</span>
            {!isDiagonal && Number.isFinite(cellData?.r) && (
              <span style={{ fontSize: 12, fontWeight: 700, color: rColor(cellData!.r!), fontFamily: "var(--font-mono, monospace)" }}>
                r = {cellData!.r!.toFixed(3)}
              </span>
            )}
            {is3D && cellData?.surfaceQuality != null && (
              <span style={{
                fontSize: 11, fontWeight: 600, fontFamily: "var(--font-mono, monospace)",
                color: surfaceQualityColor(cellData.surfaceQuality),
                padding: "1px 6px", borderRadius: 4,
                background: "var(--gray-a3)",
              }}>
                surface: {(cellData.surfaceQuality * 100).toFixed(0)}%
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", color: "var(--gray-9)", fontSize: 18, cursor: "pointer", padding: "2px 6px", lineHeight: 1, flexShrink: 0 }}
          >
            &times;
          </button>
        </div>
        <div style={{ position: "relative", background: "var(--gray-2, #1c1c22)", borderRadius: 8, padding: 4 }}>
          {isDiagonal
            ? (diagPlotType === "boxplot"
              ? <BoxPlotCellModal values={cellData?.values ?? []} size={chartSize} label={rowCol.header} />
              : diagPlotType === "violin"
              ? <ViolinCellModal values={cellData?.values ?? []} size={chartSize} label={rowCol.header} />
              : <HistogramCellModal values={cellData?.values ?? []} size={chartSize} label={rowCol.header} showAxisLabels />)
            : is3D
            ? <Scatter3DCellModal
                data={cellData?.triples ?? []}
                size={chartSize}
                r={cellData?.r ?? NaN}
                rPartial={cellData?.rPartial}
                multiR={cellData?.multiR}
                xLabel={colCol.header}
                yLabel={rowCol.header}
                zLabel={zAxisKey}
                colorByValues={colorByValues}
                colorByNumbers={colorByNumbers}
                groupColorMap={groupColorMap}
                onPointClick={onPointClick}
                selectedRowIdx={selectedRowIdx}
              />
            : (offDiagPlotType === "line"
              ? <LineCellModal data={cellData?.pairs ?? []} size={chartSize} xLabel={colCol.header} yLabel={rowCol.header} colorByValues={colorByValues} groupColorMap={groupColorMap} />
              : offDiagPlotType === "hexbin"
              ? <HexbinCellModal data={cellData?.pairs ?? []} size={chartSize} xLabel={colCol.header} yLabel={rowCol.header} />
              : <ScatterCellModal data={cellData?.pairs ?? []} size={chartSize} r={cellData?.r ?? NaN} colorByValues={colorByValues} colorByNumbers={colorByNumbers} onPointClick={onPointClick} selectedRowIdx={selectedRowIdx} xLabel={colCol.header} yLabel={rowCol.header} groupColorMap={groupColorMap} />)
          }
          {!isDiagonal && (
            <button
              onClick={handleOpenInBuilder}
              style={{
                position: "absolute", bottom: 16, right: 16,
                padding: "5px 14px", borderRadius: 6,
                border: "1px solid var(--accent-7, #4f46e5)",
                background: "var(--color-surface, rgba(26,26,46,0.9))",
                color: "var(--accent-11, #a5b4fc)",
                fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
                opacity: 0.7, transition: "opacity 150ms",
              }}
              onMouseEnter={e => { e.currentTarget.style.opacity = "1"; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = "0.7"; }}
            >
              {is3D ? "Open 3D in Plot Builder" : "Open in Plot Builder"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hover info panel (right sidebar)
// ---------------------------------------------------------------------------

interface HoverInfo {
  row: number;
  col: number;
  point?: { x: number; y: number; idx: number };
}

const INFO_PANEL_W = 200;

const LABEL_STYLE: React.CSSProperties = {
  fontSize: 10, color: "var(--gray-9, #6b7280)", textTransform: "uppercase",
  letterSpacing: "0.05em", marginBottom: 2,
};
const VALUE_STYLE: React.CSSProperties = {
  fontSize: 13, color: "var(--gray-12, #f0f0f5)", fontWeight: 600,
  fontFamily: "var(--font-mono, monospace)", wordBreak: "break-all",
};

// ---------------------------------------------------------------------------
// Contextual guide — replaces idle text in hover panel
// ---------------------------------------------------------------------------

const GUIDE_HEAD: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, color: "var(--gray-11, #c0c0c8)",
  textTransform: "uppercase", letterSpacing: "0.04em",
  marginTop: 6, marginBottom: 3,
};

const GUIDE_TEXT: React.CSSProperties = {
  fontSize: 10, lineHeight: 1.5, color: "var(--gray-9, #6b7280)",
};

const GUIDE_STAT: React.CSSProperties = {
  fontFamily: "var(--font-mono, monospace)", fontWeight: 700,
};

const GUIDE_EXAMPLE: React.CSSProperties = {
  fontSize: 9, lineHeight: 1.4, color: "var(--gray-8, #4b5563)",
  borderLeft: "2px solid var(--gray-a4)", paddingLeft: 6,
  marginTop: 3, marginBottom: 2,
};

function CorrelogramGuide({ zAxisKey }: { zAxisKey?: string }): ReactNode {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{ paddingTop: 4, display: "flex", flexDirection: "column", gap: 2 }}>
      <div style={{ ...GUIDE_TEXT, marginBottom: 4 }}>
        Hover over the matrix to see cell and point details.
      </div>

      <button
        onClick={() => setExpanded(v => !v)}
        style={{
          background: "none", border: "1px solid var(--gray-a4)",
          borderRadius: 4, padding: "3px 6px", cursor: "pointer",
          fontSize: 10, color: "var(--accent-11, #a5b4fc)",
          textAlign: "left",
        }}
      >
        {expanded ? "▾ Hide guide" : "▸ How to read this"}
      </button>

      {expanded && (
        <div style={{ display: "flex", flexDirection: "column", gap: 1, marginTop: 4 }}>
          {/* --- Basics --- */}
          <div style={GUIDE_HEAD}>Basics</div>
          <div style={GUIDE_TEXT}>
            Each cell shows the relationship between two variables.
            <strong> Diagonal</strong> = distribution of one variable.
            <strong> Off-diagonal</strong> = how two variables relate.
          </div>

          <div style={GUIDE_HEAD}>Pearson r</div>
          <div style={GUIDE_TEXT}>
            <span style={{ ...GUIDE_STAT, color: "#34d399" }}>r</span> measures
            linear correlation from −1 to +1.
          </div>
          <div style={GUIDE_EXAMPLE}>
            r = 0.95 → strong positive trend<br />
            r = −0.3 → weak inverse trend<br />
            r ≈ 0 → no linear link
          </div>

          {/* --- 3D-specific guide --- */}
          {zAxisKey && (
            <>
              <div style={{ borderTop: "1px solid var(--gray-a4)", marginTop: 6, paddingTop: 6 }} />
              <div style={{ ...GUIDE_HEAD, color: "var(--blue-11)" }}>
                3D mode (Z = {zAxisKey})
              </div>

              <div style={GUIDE_HEAD}>Partial correlation r<sub>p</sub></div>
              <div style={GUIDE_TEXT}>
                <span style={{ ...GUIDE_STAT, color: "#60a5fa" }}>r<sub>p</sub></span> is
                the correlation between X and Y <em>after removing Z&apos;s influence</em>.
                Compare it with r:
              </div>
              <div style={GUIDE_EXAMPLE}>
                r = 0.8, r<sub>p</sub> = 0.2 → Z explains most of the
                X-Y link. Without Z, X and Y are nearly independent.
              </div>
              <div style={GUIDE_EXAMPLE}>
                r = 0.1, r<sub>p</sub> = 0.7 → X and Y look uncorrelated
                in 2D, but are strongly linked once you account for Z.
                This is <strong>Simpson&apos;s paradox</strong>.
              </div>
              <div style={GUIDE_EXAMPLE}>
                r ≈ r<sub>p</sub> → Z has no effect on the X-Y
                relationship. The coupling is independent of Z.
              </div>

              <div style={GUIDE_HEAD}>Simpson&apos;s paradox</div>
              <div style={GUIDE_TEXT}>
                A trend that appears in 2D can reverse or vanish when a
                third variable is accounted for — or vice versa. The
                partial correlation catches this automatically.
              </div>
              <div style={GUIDE_EXAMPLE}>
                Example: drag increases with speed (r &gt; 0), but after
                controlling for angle-of-attack (Z), drag may <em>decrease</em>{" "}
                at each fixed angle — the overall trend was driven by
                angle, not speed alone.
              </div>

              <div style={GUIDE_HEAD}>Multiple R</div>
              <div style={GUIDE_TEXT}>
                <span style={{ ...GUIDE_STAT, color: "#a78bfa" }}>R</span> measures
                how well X and Z <em>together</em> predict Y. High R means the
                three axes form a clean surface (plane, bowl, saddle).
              </div>

              <div style={GUIDE_HEAD}>Surface quality</div>
              <div style={GUIDE_TEXT}>
                A composite score combining R and the partial-r shift.
                Cells with high surface quality are the most
                &ldquo;interesting&rdquo; 3D relationships.
              </div>
              <div style={GUIDE_EXAMPLE}>
                <span style={{ color: "#34d399" }}>■</span> &gt;60% → Strong 3D coupling<br />
                <span style={{ color: "#fbbf24" }}>■</span> 30–60% → Moderate structure<br />
                <span style={{ color: "#60a5fa" }}>■</span> &lt;30% → Weak 3D relationship
              </div>

              <div style={GUIDE_HEAD}>Which axes are coupled?</div>
              <div style={GUIDE_TEXT}>
                Look for cells where:
              </div>
              <div style={GUIDE_EXAMPLE}>
                1. r<sub>p</sub> ≠ r → Z mediates or confounds<br />
                2. Surface quality is high → clean 3D shape<br />
                3. R is high but r is low → hidden 3D structure<br />
                Click any cell to explore the 3D scatter interactively.
              </div>
            </>
          )}

          {!zAxisKey && (
            <>
              <div style={{ borderTop: "1px solid var(--gray-a4)", marginTop: 6, paddingTop: 4 }} />
              <div style={GUIDE_TEXT}>
                <strong>Tip:</strong> Select a <span style={{ color: "var(--blue-11)" }}>Z axis</span>{" "}
                above to enable 3D mode. Each cell becomes a 3D scatter
                plot, and you&apos;ll see partial correlations that reveal
                hidden coupling between axes.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function HoverInfoPanel({
  hoverRef,
  numericColumns,
  matrixData,
  rows,
  zAxisKey,
}: {
  hoverRef: React.RefObject<HoverInfo | null>;
  numericColumns: CorrelogramColumn[];
  matrixData: Record<string, { pairs?: { x: number; y: number; idx: number }[]; triples?: { x: number; y: number; z: number; idx: number }[]; values?: number[]; r?: number; rPartial?: number; multiR?: number; surfaceQuality?: number }>;
  rows: Record<string, unknown>[];
  zAxisKey?: string;
}) {
  const [, tick] = useState(0);
  const prev = useRef<string>("");

  useEffect(() => {
    let raf: number;
    const poll = () => {
      const key = hoverRef.current
        ? `${hoverRef.current.row}-${hoverRef.current.col}-${hoverRef.current.point?.idx ?? ""}`
        : "";
      if (key !== prev.current) {
        prev.current = key;
        tick(c => c + 1);
      }
      raf = requestAnimationFrame(poll);
    };
    raf = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(raf);
  }, [hoverRef]);

  const hover = hoverRef.current;
  const rowCol = hover ? numericColumns[hover.row] : null;
  const colCol = hover ? numericColumns[hover.col] : null;
  const isDiag = hover ? hover.row === hover.col : false;
  const cellData = hover ? matrixData[`${hover.row}-${hover.col}`] : null;
  const hoveredRow = hover?.point != null ? rows[hover.point.idx] : null;
  const runName = hoveredRow?.name as string | undefined;

  return (
    <div style={{
      width: INFO_PANEL_W, flexShrink: 0,
      borderLeft: "1px solid var(--gray-a4, #2a2a4a)",
      padding: "12px 10px",
      display: "flex", flexDirection: "column", gap: 12,
      fontSize: 12, color: "var(--gray-11, #9ca3af)",
      overflow: "hidden",
    }}>
      {!hover && (
        <CorrelogramGuide zAxisKey={zAxisKey} />
      )}

      {hover && isDiag && rowCol && (
        <>
          <div>
            <div style={LABEL_STYLE}>Histogram</div>
            <div style={VALUE_STYLE}>{rowCol.header}</div>
          </div>
          <div>
            <div style={LABEL_STYLE}>Points</div>
            <div style={VALUE_STYLE}>{cellData?.values?.length ?? 0}</div>
          </div>
        </>
      )}

      {hover && !isDiag && rowCol && colCol && (
        <>
          <div>
            <div style={LABEL_STYLE}>Plot</div>
            <div style={{ ...VALUE_STYLE, fontSize: 12 }}>
              {colCol.header} <span style={{ color: "var(--gray-8)", fontWeight: 400 }}>vs</span> {rowCol.header}
            </div>
          </div>
          {cellData?.r != null && Number.isFinite(cellData.r) && (
            <div>
              <div style={LABEL_STYLE}>Correlation (r)</div>
              <div style={{ ...VALUE_STYLE, color: rColor(cellData.r!) }}>
                {cellData.r!.toFixed(4)}
              </div>
            </div>
          )}
          {cellData?.rPartial != null && Number.isFinite(cellData.rPartial) && (
            <div>
              <div style={LABEL_STYLE}>Partial r (ctrl Z)</div>
              <div style={{ ...VALUE_STYLE, color: partialRColor(cellData.r ?? 0, cellData.rPartial!) }}>
                {cellData.rPartial!.toFixed(4)}
              </div>
              <div style={{ fontSize: 9, color: "var(--gray-8)", marginTop: 1 }}>
                {Math.abs((cellData.r ?? 0) - Math.abs(cellData.rPartial!)) > 0.2
                  ? "Z explains much of this coupling"
                  : "Axes coupled independently of Z"}
              </div>
            </div>
          )}
          {cellData?.multiR != null && Number.isFinite(cellData.multiR) && (
            <div>
              <div style={LABEL_STYLE}>Multiple R</div>
              <div style={{ ...VALUE_STYLE, color: "#a78bfa" }}>
                {cellData.multiR!.toFixed(4)}
              </div>
            </div>
          )}
          {cellData?.surfaceQuality != null && (
            <div>
              <div style={LABEL_STYLE}>Surface quality</div>
              <div style={{
                ...VALUE_STYLE,
                color: surfaceQualityColor(cellData.surfaceQuality),
                display: "flex", alignItems: "center", gap: 6,
              }}>
                {(cellData.surfaceQuality * 100).toFixed(0)}%
                <div style={{
                  flex: 1, height: 4, borderRadius: 2,
                  background: "var(--gray-a4)",
                  overflow: "hidden",
                }}>
                  <div style={{
                    width: `${cellData.surfaceQuality * 100}%`,
                    height: "100%",
                    borderRadius: 2,
                    background: surfaceQualityColor(cellData.surfaceQuality),
                  }} />
                </div>
              </div>
              <div style={{ fontSize: 9, color: "var(--gray-8)", marginTop: 1 }}>
                {cellData.surfaceQuality > 0.6
                  ? "Strong 3D coupling"
                  : cellData.surfaceQuality > 0.3
                  ? "Moderate 3D structure"
                  : "Weak 3D relationship"}
              </div>
            </div>
          )}
          <div>
            <div style={LABEL_STYLE}>Points</div>
            <div style={VALUE_STYLE}>{cellData?.triples?.length ?? cellData?.pairs?.length ?? 0}</div>
          </div>
        </>
      )}

      {hover?.point != null && rowCol && colCol && !isDiag && (
        <>
          <div style={{ borderTop: "1px solid var(--gray-a4, #2a2a4a)", paddingTop: 10 }}>
            <div style={LABEL_STYLE}>{colCol.header}</div>
            <div style={VALUE_STYLE}>{shortLabel(hover.point.x)}</div>
          </div>
          <div>
            <div style={LABEL_STYLE}>{rowCol.header}</div>
            <div style={VALUE_STYLE}>{shortLabel(hover.point.y)}</div>
          </div>
          {runName && (
            <div>
              <div style={LABEL_STYLE}>Run</div>
              <div style={{ ...VALUE_STYLE, fontSize: 11, fontWeight: 500 }}>{runName}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export function Correlogram({
  rows, numericColumns, groups, colorByKey, zAxisKey,
  onPointClick, onPointContextMenu, selectedRowIdx,
  offDiagPlotType: offDiagProp, diagPlotType: diagProp,
  onOffDiagPlotTypeChange, onDiagPlotTypeChange,
}: CorrelogramProps): ReactNode {
  const [localOffDiag, setLocalOffDiag] = useState<OffDiagPlotType>("scatter");
  const [localDiag, setLocalDiag] = useState<DiagPlotType>("histogram");

  // Auto-switch to scatter3d when z-axis is selected
  const prevZAxisKey = useRef(zAxisKey);
  useEffect(() => {
    if (zAxisKey && !prevZAxisKey.current) {
      // Z axis just turned on → switch to 3D
      if (onOffDiagPlotTypeChange) onOffDiagPlotTypeChange("scatter3d");
      else setLocalOffDiag("scatter3d");
    } else if (!zAxisKey && prevZAxisKey.current) {
      // Z axis just turned off → switch back to scatter
      if (onOffDiagPlotTypeChange) onOffDiagPlotTypeChange("scatter");
      else setLocalOffDiag("scatter");
    }
    prevZAxisKey.current = zAxisKey;
  }, [zAxisKey, onOffDiagPlotTypeChange]);
  const offDiagPlotType = offDiagProp ?? localOffDiag;
  const diagPlotType = diagProp ?? localDiag;
  const handleOffDiagChange = onOffDiagPlotTypeChange ?? setLocalOffDiag;
  const handleDiagChange = onDiagPlotTypeChange ?? setLocalDiag;
  const zoomRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const resetBtnRef = useRef<HTMLButtonElement>(null);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [expandedCell, setExpandedCell] = useState<{ row: number; col: number } | null>(null);
  const hoverRef = useRef<HoverInfo | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const containerRef = useCallback((el: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setContainerSize({ width: rect.width, height: rect.height });
    }
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
      }
    });
    observer.observe(el);
    observerRef.current = observer;
  }, []);

  useEffect(() => () => { observerRef.current?.disconnect(); }, []);

  const { reset, wasDrag } = useZoomPan(zoomRef, innerRef, resetBtnRef);

  const n = numericColumns.length;

  const cellSize = useMemo(() => {
    if (n === 0 || containerSize.width === 0 || containerSize.height === 0) return 0;
    const availW = containerSize.width - LABEL_COL_W - INFO_PANEL_W - 40;
    const availH = containerSize.height - HEADER_ROW_H - TICK_ROW_H - 60;
    return Math.max(24, Math.floor(Math.min(availW / n, availH / n)));
  }, [n, containerSize]);

  const colRanges = useMemo<(ColRange | null)[]>(() =>
    numericColumns.map(col => computeRange(extractNumbers(rows, col.key))),
  [rows, numericColumns]);

  const colorByMode = useMemo<"numeric" | "categorical" | "none">(() => {
    if (!colorByKey) return "none";
    const sample = rows.slice(0, 50);
    const numericCount = sample.filter(r => typeof r[colorByKey] === "number" && Number.isFinite(r[colorByKey] as number)).length;
    return numericCount > sample.length * 0.5 ? "numeric" : "categorical";
  }, [rows, colorByKey]);

  const colorByValues = useMemo(() => {
    if (colorByMode !== "categorical" || !colorByKey) return undefined;
    return rows.map(r => String(r[colorByKey] ?? ""));
  }, [rows, colorByKey, colorByMode]);

  const colorByNumbers = useMemo(() => {
    if (colorByMode !== "numeric" || !colorByKey) return undefined;
    return rows.map(r => {
      const v = r[colorByKey];
      return typeof v === "number" && Number.isFinite(v) ? v : NaN;
    });
  }, [rows, colorByKey, colorByMode]);

  const groupColorByValues = useMemo(() => {
    if (!groups || groups.length <= 1) return undefined;
    const rowToGroup = new Map<number, string>();
    for (const g of groups) {
      for (const row of g.rows) {
        const idx = rows.indexOf(row);
        if (idx >= 0) rowToGroup.set(idx, g.key);
      }
    }
    return rows.map((_, i) => rowToGroup.get(i) ?? "");
  }, [groups, rows]);

  const groupColorMap = useMemo(() => {
    if (!groups || groups.length <= 1) return undefined;
    const map = new Map<string, string>();
    for (const g of groups) map.set(g.key, g.color);
    return map;
  }, [groups]);

  const effectiveColorByValues = groupColorByValues ?? colorByValues;
  const effectiveColorByNumbers = groupColorByValues ? undefined : colorByNumbers;

  // Lazy correlogram: compute cell data on first access instead of eagerly
  // computing all n² cells upfront (which can generate millions of objects).
  const matrixData = useMemo(() => {
    type CellData = {
      pairs?: { x: number; y: number; idx: number }[];
      triples?: { x: number; y: number; z: number; idx: number }[];
      values?: number[];
      r?: number;
      rPartial?: number;
      multiR?: number;
      surfaceQuality?: number;
    };
    const cache: Record<string, CellData> = {};

    const zValsAll = zAxisKey ? rows.map(r => {
      const v = r[zAxisKey];
      return typeof v === "number" && Number.isFinite(v) ? v : NaN;
    }) : null;

    function computeCell(key: string): CellData {
      const [si, sj] = key.split("-");
      const i = Number(si);
      const j = Number(sj);
      if (Number.isNaN(i) || Number.isNaN(j) || i >= n || j >= n) return {};

      if (i === j) {
        const colI = numericColumns[i]!;
        const diag: CellData = { values: extractNumbers(rows, colI.key) };
        cache[key] = diag;
        return diag;
      }

      // Compute the canonical (min,max) pair and derive both orientations
      const ci = Math.min(i, j);
      const cj = Math.max(i, j);
      const canonKey = `${ci}-${cj}`;
      // If the canonical pair was already computed (via the mirror), return it
      if (canonKey in cache) return cache[key]!;

      const colI = numericColumns[ci]!;
      const colJ = numericColumns[cj]!;
      const pairs = extractPairs(rows, colI.key, colJ.key);
      const xVals = pairs.map(p => p.x);
      const yVals = pairs.map(p => p.y);
      const corr = pearsonCorrelation(xVals, yVals);

      let triples: { x: number; y: number; z: number; idx: number }[] | undefined;
      let triplesSwapped: { x: number; y: number; z: number; idx: number }[] | undefined;
      let rPartial: number | undefined;
      let multiR: number | undefined;
      if (zAxisKey && zValsAll) {
        triples = extractTriples(rows, colI.key, colJ.key, zAxisKey);
        triplesSwapped = triples.map(t => ({ x: t.y, y: t.x, z: t.z, idx: t.idx }));
        const triX = triples.map(t => t.x);
        const triY = triples.map(t => t.y);
        const triZ = triples.map(t => t.z);
        rPartial = partialCorrelation(triX, triY, triZ);
        multiR = multipleCorrelationR(triX, triY, triZ);
      }

      let surfaceQuality: number | undefined;
      if (multiR != null && rPartial != null && Number.isFinite(multiR) && Number.isFinite(rPartial)) {
        const partialShift = Math.abs(Math.abs(corr) - Math.abs(rPartial));
        surfaceQuality = 0.6 * multiR + 0.4 * partialShift;
      }

      const swapped = pairs.map(p => ({ x: p.y, y: p.x, idx: p.idx }));
      // Cell (ci,cj): X = column cj, Y = row ci → pairs are swapped
      cache[`${ci}-${cj}`] = { pairs: swapped, triples: triplesSwapped, r: corr, rPartial, multiR, surfaceQuality };
      cache[`${cj}-${ci}`] = { pairs, triples, r: corr, rPartial, multiR, surfaceQuality };
      return cache[key]!;
    }

    return new Proxy(cache, {
      get(target, prop) {
        if (typeof prop !== "string") return Reflect.get(target, prop);
        if (!(prop in target)) computeCell(prop);
        return target[prop];
      },
    });
  }, [rows, numericColumns, n, zAxisKey]);

  // Whether to enable lazy rendering for off-screen cells (many cells)
  const useLazy = n * n > LAZY_THRESHOLD;

  // Auto-hexbin: when N > 2000 and user selected "scatter", use hexbin for grid cells
  const autoHexbin = rows.length > 2000 && offDiagPlotType === "scatter";

  // Whether to use canvas scatter (N > 200) instead of SVG Recharts
  const useCanvasScatter = rows.length > 200;

  const handleCellClick = useCallback((i: number, j: number) => {
    if (wasDrag()) return;
    setExpandedCell({ row: i, col: j });
  }, [wasDrag]);

  const handleGridContextMenu = useCallback((e: React.MouseEvent) => {
    const pt = hoverRef.current?.point;
    if (!pt || !onPointContextMenu) return;
    e.preventDefault();
    e.stopPropagation();
    onPointContextMenu(pt.idx, e);
  }, [onPointContextMenu]);

  if (n === 0) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--gray-9, #6b7280)", flexDirection: "column", gap: 8, padding: 40 }}>
        <div style={{ fontSize: 18 }}>No numeric columns to plot</div>
        <div style={{ fontSize: 13 }}>Select at least two numeric fields above.</div>
      </div>
    );
  }

  if (n > 20) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--gray-9, #6b7280)", flexDirection: "column", gap: 8, padding: 40 }}>
        <div style={{ fontSize: 18 }}>Too many columns ({n})</div>
        <div style={{ fontSize: 13 }}>Deselect some columns to bring the count to 20 or fewer.</div>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "4px 12px", fontSize: 11, color: "var(--gray-9, #6b7280)", flexShrink: 0, flexWrap: "wrap" }}>
        <span>{n} variables</span>
        <span>&middot;</span>
        <span>{rows.length} data point{rows.length !== 1 ? "s" : ""}</span>
        {groups && groups.length > 1 && (
          <>
            <span>&middot;</span>
            <span>{groups.length} groups</span>
          </>
        )}
        <span>&middot;</span>
        <span style={{ fontFamily: "var(--font-mono, monospace)" }}>r = Pearson correlation</span>
        {zAxisKey && (
          <>
            <span>&middot;</span>
            <span style={{ fontFamily: "var(--font-mono, monospace)", color: "var(--blue-11)" }}>
              Z = {zAxisKey} &middot; r<sub>p</sub> = partial correlation
            </span>
          </>
        )}
        <span>&middot;</span>
        <span>scroll to zoom, drag to pan, click to enlarge</span>
        <span style={{ marginLeft: "auto" }} />
        <label style={{ display: "flex", alignItems: "center", gap: 3 }}>
          <span>Plot:</span>
          <select
            value={offDiagPlotType}
            onChange={(e) => handleOffDiagChange(e.target.value as OffDiagPlotType)}
            style={{
              padding: "1px 4px", border: "1px solid var(--gray-a5)", borderRadius: 3,
              background: "var(--color-surface)", color: "var(--gray-12)", fontSize: 11,
            }}
          >
            <option value="scatter">Scatter</option>
            <option value="line">Line</option>
            <option value="hexbin">Hexbin</option>
            <option value="scatter3d" disabled={!zAxisKey}>3D Scatter{!zAxisKey ? " (set Z)" : ""}</option>
          </select>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 3 }}>
          <span>Diag:</span>
          <select
            value={diagPlotType}
            onChange={(e) => handleDiagChange(e.target.value as DiagPlotType)}
            style={{
              padding: "1px 4px", border: "1px solid var(--gray-a5)", borderRadius: 3,
              background: "var(--color-surface)", color: "var(--gray-12)", fontSize: 11,
            }}
          >
            <option value="histogram">Histogram</option>
            <option value="boxplot">Box Plot</option>
            <option value="violin">Violin/KDE</option>
          </select>
        </label>
      </div>

      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        <div
          ref={zoomRef}
          onContextMenu={handleGridContextMenu}
          style={{ flex: 1, overflow: "hidden", position: "relative", cursor: "grab", touchAction: "none", userSelect: "none" }}
        >
          <button
            ref={resetBtnRef}
            onClick={(e) => { e.stopPropagation(); reset(); }}
            style={{
              display: "none",
              position: "absolute", top: 8, right: 8, zIndex: 10,
              padding: "4px 10px", borderRadius: 6,
              background: "var(--gray-a3)", border: "1px solid var(--gray-a5)",
              color: "var(--gray-11)", fontSize: 11, cursor: "pointer",
            }}
          >
            Reset zoom
          </button>

          <div ref={innerRef} style={{ transformOrigin: "0 0", padding: "8px 12px" }}>
            <div style={{
              display: "grid",
              gridTemplateColumns: `${LABEL_COL_W}px repeat(${n}, ${cellSize}px)`,
              gridTemplateRows: `${HEADER_ROW_H}px repeat(${n}, ${cellSize}px) ${TICK_ROW_H}px`,
              gap: 1,
              width: "fit-content",
            }}>
              <div />

              {numericColumns.map((col, j) => (
                <div key={`col-header-${j}`} style={{
                  display: "flex", alignItems: "flex-end", justifyContent: "center",
                  fontSize: 9, fontWeight: 600,
                  color: "var(--gray-11, #9ca3af)", overflow: "hidden", textOverflow: "ellipsis",
                  whiteSpace: "nowrap", padding: "0 1px",
                }} title={col.key}>
                  {col.header}
                </div>
              ))}

              {numericColumns.map((rowCol, i) => (
                <Fragment key={`row-${i}`}>
                  <div style={{
                    display: "flex", flexDirection: "column", alignItems: "flex-end", justifyContent: "center",
                    paddingRight: 4, overflow: "hidden",
                  }} title={rowCol.key}>
                    <span style={{
                      fontSize: 9, fontWeight: 600, lineHeight: 1.1,
                      color: "var(--gray-11, #9ca3af)",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                      maxWidth: LABEL_COL_W - 4, textAlign: "right",
                    }}>
                      {rowCol.header}
                    </span>
                    {colRanges[i] && (
                      <span style={{ fontSize: 7, color: "var(--gray-8, #4b5563)", fontFamily: "var(--font-mono, monospace)", lineHeight: 1, marginTop: 1 }}>
                        {shortLabel(colRanges[i]!.min)}&ndash;{shortLabel(colRanges[i]!.max)}
                      </span>
                    )}
                  </div>

                  {numericColumns.map((colCol, j) => {
                    const cellData = matrixData[`${i}-${j}`];
                    if (i === j) {
                      const diagContent = (
                        <div
                          key={`cell-${i}-${j}`}
                          onClick={() => handleCellClick(i, j)}
                          onMouseEnter={() => { hoverRef.current = { row: i, col: j }; }}
                          onMouseLeave={() => { hoverRef.current = null; }}
                          title={`${rowCol.header} distribution`}
                          style={{ width: cellSize, height: cellSize, background: "var(--accent-a2, rgba(99,102,241,0.03))", cursor: "pointer" }}
                        >
                          {diagPlotType === "boxplot"
                            ? <BoxPlotCellGrid values={cellData?.values ?? []} size={cellSize} />
                            : diagPlotType === "violin"
                            ? <ViolinCellGrid values={cellData?.values ?? []} size={cellSize} />
                            : <HistogramCellGrid values={cellData?.values ?? []} size={cellSize} label={rowCol.header} />}
                        </div>
                      );
                      return useLazy
                        ? <LazyCell key={`cell-${i}-${j}`} size={cellSize}>{diagContent}</LazyCell>
                        : <Fragment key={`cell-${i}-${j}`}>{diagContent}</Fragment>;
                    }
                    const rVal = cellData?.r;
                    const rStr = Number.isFinite(rVal) ? `  (r = ${rVal!.toFixed(3)})` : "";

                    // Determine which scatter renderer to use for this cell
                    const renderScatter = () => {
                      const pairs = cellData?.pairs ?? [];
                      // Auto-hexbin: N > 2000 and user hasn't explicitly chosen a type
                      if (autoHexbin) {
                        return <HexbinCellGrid data={pairs} size={cellSize} />;
                      }
                      // Canvas scatter for large datasets
                      if (useCanvasScatter) {
                        return (
                          <CanvasScatterCell
                            data={pairs}
                            size={cellSize}
                            row={i}
                            col={j}
                            colorByValues={effectiveColorByValues}
                            colorByNumbers={effectiveColorByNumbers}
                            onPointClick={onPointClick}
                            onHoverPoint={hoverRef}
                            groupColorMap={groupColorMap}
                          />
                        );
                      }
                      // SVG Recharts for small datasets
                      return (
                        <ScatterCellGrid
                          data={pairs}
                          size={cellSize}
                          r={cellData?.r ?? NaN}
                          colorByValues={effectiveColorByValues}
                          colorByNumbers={effectiveColorByNumbers}
                          onPointClick={onPointClick}
                          onHoverPoint={hoverRef}
                          groupColorMap={groupColorMap}
                        />
                      );
                    };

                    const offDiagContent = (
                      <div
                        key={`cell-${i}-${j}`}
                        onClick={() => handleCellClick(i, j)}
                        onMouseEnter={() => { hoverRef.current = { row: i, col: j }; }}
                        onMouseLeave={() => { hoverRef.current = null; }}
                        title={`${colCol.header} vs ${rowCol.header}${rStr}`}
                        style={{ width: cellSize, height: cellSize, cursor: "pointer", position: "relative" }}
                      >
                        {offDiagPlotType === "scatter3d" && cellData?.triples
                          ? <Scatter3DCellGrid
                              data={cellData.triples}
                              size={cellSize}
                              colorByValues={effectiveColorByValues}
                              colorByNumbers={effectiveColorByNumbers}
                              groupColorMap={groupColorMap}
                            />
                          : offDiagPlotType === "line"
                          ? <LineCellGrid data={cellData?.pairs ?? []} size={cellSize} colorByValues={effectiveColorByValues} groupColorMap={groupColorMap} />
                          : offDiagPlotType === "hexbin"
                          ? <HexbinCellGrid data={cellData?.pairs ?? []} size={cellSize} />
                          : renderScatter()}
                        {offDiagPlotType === "scatter" && !autoHexbin && selectedRowIdx != null && cellData?.pairs && (
                          <SelectionDot data={cellData.pairs} size={cellSize} selectedRowIdx={selectedRowIdx} />
                        )}
                        {/* Surface quality badge for 3D mode */}
                        {offDiagPlotType === "scatter3d" && cellData?.surfaceQuality != null && cellSize >= 48 && (
                          <SurfaceQualityBadge
                            surfaceQuality={cellData.surfaceQuality}
                            rPartial={cellData.rPartial}
                            r={cellData.r ?? 0}
                            cellSize={cellSize}
                          />
                        )}
                      </div>
                    );

                    return useLazy
                      ? <LazyCell key={`cell-${i}-${j}`} size={cellSize}>{offDiagContent}</LazyCell>
                      : <Fragment key={`cell-${i}-${j}`}>{offDiagContent}</Fragment>;
                  })}
                </Fragment>
              ))}

              <div />
              {numericColumns.map((_, j) => (
                <BottomTickCell key={`btick-${j}`} range={colRanges[j] ?? null} size={cellSize} />
              ))}
            </div>
          </div>

          {expandedCell && (
            <ExpandedCellOverlay
              row={expandedCell.row}
              col={expandedCell.col}
              numericColumns={numericColumns}
              matrixData={matrixData}
              containerSize={containerSize}
              colorByValues={effectiveColorByValues}
              colorByNumbers={effectiveColorByNumbers}
              groupColorMap={groupColorMap}
              zAxisKey={zAxisKey}
              offDiagPlotType={offDiagPlotType}
              diagPlotType={diagPlotType}
              onClose={() => setExpandedCell(null)}
              onPointClick={onPointClick}
              selectedRowIdx={selectedRowIdx}
            />
          )}
        </div>

        {/* Hover info panel */}
        <HoverInfoPanel hoverRef={hoverRef} numericColumns={numericColumns} matrixData={matrixData} rows={rows} zAxisKey={zAxisKey} />

        {/* Group legend — right sidebar */}
        {groups && groups.length > 1 && (
          <div style={{
            display: "flex", flexDirection: "column", gap: 6, padding: "8px 10px",
            fontSize: 11, flexShrink: 0, overflowY: "auto", maxWidth: 220, minWidth: 120,
            borderLeft: "1px solid var(--gray-a4)",
          }}>
            {groups.map((g) => (
              <span key={g.key} style={{ display: "flex", alignItems: "flex-start", gap: 5 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: g.color, flexShrink: 0, marginTop: 2 }} />
                <span style={{ color: "var(--gray-11, #9ca3af)", lineHeight: 1.3, wordBreak: "break-word" }}>{g.label}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Correlogram;
